import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Calendar } from 'primereact/calendar';
import { SelectButton } from 'primereact/selectbutton';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import baseURL, { endPoints } from '../../Config';
import usePreferredLanguage from '../../utilities/usePreferredLanguage';
import { translateUiText, translateUiToken } from '../../utilities/siteTranslations';
import { getQuickBooksViewAccess } from '../../utilities/quickbooksAccess';
import OkrFinancialIndicators from '../OKR/OkrFinancialIndicators';

const BUSINESS_UNIT_OPTIONS = [
    { label: 'Consolidated', value: 'consolidated' },
    { label: 'Foods', value: 'foods' },
    { label: 'Empaque', value: 'empaque' },
    { label: 'Coffee & Vending', value: 'coffee_vending' }
];

const TEAM_OPTIONS = [
    { label: 'Comercial', value: 'comercial' },
    { label: 'Gerencia', value: 'gerencia' },
    { label: 'Finanzas', value: 'finanzas' }
];

const QUOTA_SUPPORTED_UNITS = new Set(['empaque', 'foods']);
const QUARTER_OPTIONS = [
    { label: 'Q1', value: 1 },
    { label: 'Q2', value: 2 },
    { label: 'Q3', value: 3 },
    { label: 'Q4', value: 4 }
];
const QUOTA_PACE_PERIOD_OPTIONS = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Weekly', value: 'weekly' }
];
const CROSS_SELLING_STATUS_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'New family', value: 'New family' },
    { label: 'New category', value: 'New category' },
    { label: 'New customer', value: 'New customer' },
    { label: 'No change', value: 'No change' }
];
const CROSS_SELLING_VIEW_OPTIONS = [
    { label: 'Detected', value: 'detected' },
    { label: 'Opportunities', value: 'opportunities' }
];
const OPPORTUNITY_PRIORITY_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' }
];

const DEFAULT_CASH_FLOW_COLLECTION_PROBABILITIES = {
    empaque: { '0-30': 63, '31-60': 32, '61-90': 66, '90+': 0 },
    foods: { '0-30': 60, '31-60': 100, '61-90': 100, '90+': 0 },
    coffee_vending: { '0-30': 92, '31-60': 92, '61-90': 96, '90+': 0 }
};

const PAYABLE_PAYMENT_STATUS_OPTIONS = [
    { label: 'Pendiente', value: 'pending' },
    { label: 'Agendado', value: 'scheduled' },
    { label: 'Pagado parcial', value: 'partial_paid' },
    { label: 'Retener', value: 'hold' },
    { label: 'En disputa', value: 'dispute' }
];

const createEmptyWeeklyManualValues = () => [0, 0, 0, 0];
const REPURCHASE_IMPORTANCE_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'Critical', value: 'Critical' },
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' }
];

const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString();
};

const formatDateOnlyValue = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return value || '-';
    return `${match[3]}/${match[2]}/${match[1]}`;
};

const formatCount = (value) => {
    const numeric = Number(value || 0);
    return numeric.toLocaleString('en-US');
};

const formatMoney = (value) => {
    if (value == null || value === '') return '-';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    return numeric.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const formatPercent = (value) => {
    if (value == null || value === '') return '-';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    return `${numeric.toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    })}%`;
};

const getConnectionSeverity = (connection) => {
    if (!connection?.connected) return 'danger';
    if (connection?.lastRefreshError) return 'warning';
    return 'success';
};

const getConnectionLabel = (connection) => {
    if (!connection?.connected) return 'Disconnected';
    if (connection?.lastRefreshError) return 'Needs attention';
    return 'Connected';
};

const toDateParam = (value, endOfDay = false) => {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
    const next = new Date(value);
    if (endOfDay) {
        next.setHours(23, 59, 59, 999);
    } else {
        next.setHours(0, 0, 0, 0);
    }
    const year = next.getFullYear();
    const month = String(next.getMonth() + 1).padStart(2, '0');
    const day = String(next.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseDateOnly = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDefaultCommercialRange = () => {
    const now = new Date();
    return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: now
    };
};

const getCurrentQuarter = () => Math.floor(new Date().getMonth() / 3) + 1;

const buildQuarterParams = (businessUnit, year, quarter) => {
    const params = new URLSearchParams();
    if (businessUnit) params.set('businessUnit', businessUnit);
    if (year) params.set('year', String(year));
    if (quarter) params.set('quarter', String(quarter));
    return params.toString();
};

const formatPeriodMonthLabel = (periodMonth) => {
    if (!periodMonth) return '-';
    const [year, month] = String(periodMonth).split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    if (Number.isNaN(date.getTime())) return periodMonth;
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const splitQuarterlyQuotaEvenly = (total) => {
    const totalCents = Math.round(Number(total || 0) * 100);
    const baseCents = Math.floor(totalCents / 3);
    const remainder = totalCents - baseCents * 3;
    return [0, 1, 2].map((index) => Number(((baseCents + (index < remainder ? 1 : 0)) / 100).toFixed(2)));
};

const parseDashboardDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getDaysInMonth = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 0;
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

const getWeekElapsedDays = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 1;
    const dayIndex = (date.getDay() + 6) % 7;
    return Math.min(dayIndex + 1, 7);
};

const getQuotaPaceSeverity = (projectedAttainment) => {
    if (projectedAttainment == null || !Number.isFinite(Number(projectedAttainment))) return 'info';
    if (Number(projectedAttainment) >= 100) return 'success';
    if (Number(projectedAttainment) >= 80) return 'warning';
    return 'danger';
};

const getQuotaPaceStatus = (projectedAttainment) => {
    if (projectedAttainment == null || !Number.isFinite(Number(projectedAttainment))) return 'No quota';
    if (Number(projectedAttainment) >= 100) return 'On pace';
    if (Number(projectedAttainment) >= 80) return 'Watch';
    return 'Behind';
};

const getCrossSellingSeverity = (status) => {
    if (status === 'New family') return 'success';
    if (status === 'New category') return 'info';
    if (status === 'New customer') return 'warning';
    return 'secondary';
};

const getOpportunityPrioritySeverity = (priority) => {
    if (priority === 'High') return 'danger';
    if (priority === 'Medium') return 'warning';
    return 'info';
};

const getRepurchaseImportanceSeverity = (importance) => {
    if (importance === 'Critical') return 'danger';
    if (importance === 'High') return 'warning';
    if (importance === 'Medium') return 'info';
    return 'secondary';
};

const formatListValue = (values = [], limit = 3) => {
    const list = Array.isArray(values) ? values.filter(Boolean) : [];
    if (!list.length) return '-';
    const visible = list.slice(0, limit).join(', ');
    return list.length > limit ? `${visible} +${list.length - limit}` : visible;
};

const buildQuotaPaceRows = (sellerRows = [], period = {}, pacePeriod = 'monthly') => {
    const endDate = parseDashboardDate(period.endDate || period.endDateText);
    const periodDays = Number(period.periodDays || 0);
    const elapsedDays = periodDays || 1;
    const monthDays = getDaysInMonth(endDate) || elapsedDays;
    const isWeekly = pacePeriod === 'weekly';
    const targetDays = isWeekly ? 7 : monthDays;
    const targetElapsedDays = isWeekly ? getWeekElapsedDays(endDate) : elapsedDays;
    const targetRemainingDays = isWeekly ? Math.max(7 - targetElapsedDays, 0) : endDate ? Math.max(monthDays - endDate.getDate(), 0) : 0;

    return sellerRows
        .filter((row) => Number(row?.quota || 0) > 0)
        .map((row) => {
            const netSales = Number(row?.netSales || 0);
            const quota = Number(row?.quota || 0);
            const currentDailyRunRate = elapsedDays ? netSales / elapsedDays : 0;
            const targetQuota = isWeekly ? (quota / monthDays) * targetDays : quota;
            const projectedClose = currentDailyRunRate * targetDays;
            const projectedAttainment = targetQuota ? (projectedClose / targetQuota) * 100 : null;
            const targetSalesToDate = isWeekly ? currentDailyRunRate * targetElapsedDays : netSales;
            const quotaRemaining = Math.max(targetQuota - targetSalesToDate, 0);
            const requiredDailyToClose = targetRemainingDays ? quotaRemaining / targetRemainingDays : quotaRemaining;

            return {
                seller: row.seller,
                currentDailyRunRate,
                projectedClose,
                projectedAttainment,
                requiredDailyToClose,
                status: getQuotaPaceStatus(projectedAttainment),
                severity: getQuotaPaceSeverity(projectedAttainment)
            };
        });
};

const buildQuotaPaceProjectionText = (row, pacePeriod = 'monthly') => {
    if (!row?.seller || row.projectedAttainment == null) return '';
    if (pacePeriod === 'weekly') {
        return `A este ritmo, ${row.seller} cierra la semana en ${formatMoney(row.projectedClose)} (${formatPercent(row.projectedAttainment)} de su ritmo esperado).`;
    }
    return `A este ritmo, ${row.seller} cierra el mes en ${formatMoney(row.projectedClose)} (${formatPercent(row.projectedAttainment)} de la cuota).`;
};

const buildCommercialParams = (businessUnit, fromDate, toDate, seller, cashFlowOverrides = {}) => {
    const params = new URLSearchParams();
    if (businessUnit && businessUnit !== 'consolidated') {
        params.set('businessUnit', businessUnit);
    }
    if (seller) params.set('seller', seller);
    const startDate = toDateParam(fromDate);
    const endDate = toDateParam(toDate, true);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (cashFlowOverrides.minimumLiquidity != null) {
        params.set('cashFlowMinimumLiquidity', String(cashFlowOverrides.minimumLiquidity));
    }
    if (cashFlowOverrides.vehicleMaintenanceReserve != null) {
        params.set('cashFlowVehicleMaintenanceReserve', String(cashFlowOverrides.vehicleMaintenanceReserve));
    }
    if (cashFlowOverrides.fixedExpenseDay != null) {
        params.set('cashFlowFixedExpenseDay', String(cashFlowOverrides.fixedExpenseDay));
    }
    if (cashFlowOverrides.vendorPaymentDays) {
        params.set('cashFlowVendorPaymentDays', JSON.stringify(cashFlowOverrides.vendorPaymentDays));
    }
    if (cashFlowOverrides.collectionProbabilities) {
        params.set('cashFlowCollectionProbabilities', JSON.stringify(cashFlowOverrides.collectionProbabilities));
    }
    if (cashFlowOverrides.manualCollections) {
        params.set('cashFlowManualCollections', JSON.stringify(cashFlowOverrides.manualCollections));
    }
    if (cashFlowOverrides.manualPayments) {
        params.set('cashFlowManualPayments', JSON.stringify(cashFlowOverrides.manualPayments));
    }
    return params.toString();
};

const getPayablePaymentStatusSeverity = (status) => {
    if (status === 'scheduled') return 'warning';
    if (status === 'partial_paid') return 'info';
    if (status === 'dispute') return 'danger';
    if (status === 'hold') return 'secondary';
    return null;
};

const getPayablePaymentStatusLabel = (status) => {
    const option = PAYABLE_PAYMENT_STATUS_OPTIONS.find((item) => item.value === status);
    return option?.label || 'Pendiente';
};

const KpiCard = ({ title, value, hint, className = 'col-12 md:col-6 xl:col-4' }) => (
    <div className={className}>
        <Card>
            <div className="text-sm text-600 mb-2">{title}</div>
            <div className="text-2xl font-semibold text-900">{value}</div>
            {hint ? <div className="text-sm text-500 mt-2">{hint}</div> : null}
        </Card>
    </div>
);

const ReceivablesAgingCard = ({
    t,
    title,
    collapsed,
    onToggle,
    total,
    rows,
    filteredRows,
    filteredTotal,
    summary,
    bucketFilter,
    bucketOptions,
    onBucketChange,
    onReviewEdit,
    savingReviewKey,
    loading,
    className = 'mt-3'
}) => (
    <Card
        title={title}
        className={`quickbooks-collapsible-card${collapsed ? ' quickbooks-collapsible-card--collapsed' : ''} ${className}`.trim()}
        header={
            <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                <div className="text-sm text-600">
                    {formatMoney(total)} | {formatCount(rows.length)} {t('open invoices')}
                </div>
                <Button
                    icon={collapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                    className="p-button-outlined p-button-sm"
                    onClick={onToggle}
                    tooltip={collapsed ? t('Expand section') : t('Collapse section')}
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        }
    >
        {!collapsed ? (
            <>
                <div className="grid align-items-end mb-3">
                    <div className="col-12 md:col-4">
                        <label className="block text-900 font-medium mb-2">{t('Days range')}</label>
                        <Dropdown value={bucketFilter} options={bucketOptions} onChange={(e) => onBucketChange(e.value)} className="w-full" />
                    </div>
                </div>
                <div className="grid mb-3">
                    {summary.map((bucket) => (
                        <div className="col-6 md:col-3" key={bucket.key}>
                            <div className="p-3 border-1 border-200 border-round h-full">
                                <div className="text-sm text-600 mb-2">{t(bucket.label)}</div>
                                <div className="text-xl font-semibold text-900">{formatMoney(bucket.amount)}</div>
                                <div className="text-xs text-500 mt-2">
                                    {formatCount(bucket.count)} {t('invoices')}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <DataTable
                    value={filteredRows}
                    responsiveLayout="scroll"
                    loading={loading}
                    emptyMessage={t('No receivables available')}
                    paginator={filteredRows.length > 10}
                    rows={10}
                >
                    <Column field="customer" header={t('Customer')} />
                    <Column field="seller" header={t('Seller')} />
                    <Column field="invoice" header={t('Invoice #')} />
                    <Column field="dueDate" header={t('Due date')} body={(rowData) => rowData?.dueDate || '-'} />
                    <Column field="amount" header={t('Amount')} body={(rowData) => formatMoney(rowData?.amount)} />
                    <Column
                        field="expectedPaymentAmount"
                        header={t('Review')}
                        body={(rowData) => (
                            <div className="flex align-items-center gap-2">
                                <div>
                                    <div className="flex align-items-center gap-2">
                                        <span className="text-sm font-semibold text-900">{formatMoney(rowData?.expectedPaymentAmount ?? rowData?.amount)}</span>
                                        {rowData?.hasReceivableReview ? <Tag value={t('Revisado')} severity="success" /> : null}
                                    </div>
                                    <div className="text-xs text-600">
                                        {formatMoney(rowData?.reviewedAmount ?? rowData?.amount)} | {formatPercent(rowData?.paymentProbability ?? 0)}
                                    </div>
                                    <div className="text-xs text-500">
                                        {t('Expected date')}: {rowData?.projectedCollectionDate || '-'}
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    icon="pi pi-pencil"
                                    className="p-button-text p-button-sm"
                                    onClick={() => onReviewEdit(rowData)}
                                    disabled={!onReviewEdit || !rowData?.quickBooksInvoiceId || savingReviewKey === rowData?.quickBooksInvoiceId}
                                    tooltip={t('Edit review')}
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                        )}
                    />
                    <Column field="days" header={t('Days overdue')} body={(rowData) => formatCount(rowData?.days)} />
                    <Column field="bucketLabel" header={t('Bucket')} body={(rowData) => t(rowData?.bucketLabel || '-')} />
                </DataTable>
                {filteredRows.length ? (
                    <div className="mt-3 text-sm text-600">
                        {t('Totals')}: {formatMoney(filteredTotal)} | {formatCount(filteredRows.length)} {t('open invoices')}
                    </div>
                ) : null}
            </>
        ) : null}
    </Card>
);

const PayablesAgingCard = ({
    t,
    title,
    collapsed,
    onToggle,
    total,
    rows,
    filteredRows,
    filteredTotal,
    summary,
    bucketFilter,
    bucketOptions,
    onBucketChange,
    onAnnotationEdit,
    savingAnnotationKey,
    loading,
    className = 'mt-3'
}) => (
    <Card
        title={title}
        className={`quickbooks-collapsible-card${collapsed ? ' quickbooks-collapsible-card--collapsed' : ''} ${className}`.trim()}
        header={
            <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                <div className="text-sm text-600">
                    {formatMoney(total)} | {formatCount(rows.length)} {t('open bills')}
                </div>
                <Button
                    icon={collapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                    className="p-button-outlined p-button-sm"
                    onClick={onToggle}
                    tooltip={collapsed ? t('Expand section') : t('Collapse section')}
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        }
    >
        {!collapsed ? (
            <>
                <div className="grid align-items-end mb-3">
                    <div className="col-12 md:col-4">
                        <label className="block text-900 font-medium mb-2">{t('Days range')}</label>
                        <Dropdown value={bucketFilter} options={bucketOptions} onChange={(e) => onBucketChange(e.value)} className="w-full" />
                    </div>
                </div>
                <div className="grid mb-3">
                    {summary.map((bucket) => (
                        <div className="col-6 md:col-3" key={bucket.key}>
                            <div className="p-3 border-1 border-200 border-round h-full">
                                <div className="text-sm text-600 mb-2">{t(bucket.label)}</div>
                                <div className="text-xl font-semibold text-900">{formatMoney(bucket.amount)}</div>
                                <div className="text-xs text-500 mt-2">
                                    {formatCount(bucket.count)} {t('bills')}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <DataTable
                    value={filteredRows}
                    responsiveLayout="scroll"
                    loading={loading}
                    emptyMessage={t('No payables available')}
                    paginator={filteredRows.length > 10}
                    rows={10}
                >
                    <Column field="vendor" header={t('Vendor')} />
                    <Column field="bill" header={t('Bill #')} />
                    <Column field="amount" header={t('Amount')} body={(rowData) => formatMoney(rowData?.amount)} />
                    <Column
                        field="paymentStatus"
                        header={t('Follow-up')}
                        body={(rowData) => (
                            <div className="flex align-items-center gap-2">
                                <Tag value={t(getPayablePaymentStatusLabel(rowData?.paymentStatus))} severity={getPayablePaymentStatusSeverity(rowData?.paymentStatus)} />
                                {rowData?.paymentStatus === 'partial_paid' ? (
                                    <span className="text-sm text-600">{formatMoney(rowData?.partialPaidAmount || 0)}</span>
                                ) : null}
                                <Button
                                    type="button"
                                    icon="pi pi-pencil"
                                    className="p-button-text p-button-sm"
                                    onClick={() => onAnnotationEdit(rowData)}
                                    disabled={!onAnnotationEdit || !rowData?.quickBooksBillId || savingAnnotationKey === rowData?.quickBooksBillId}
                                    tooltip={t('Edit follow-up')}
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                        )}
                    />
                    <Column field="days" header={t('Days overdue')} body={(rowData) => formatCount(rowData?.days)} />
                    <Column field="bucketLabel" header={t('Bucket')} body={(rowData) => t(rowData?.bucketLabel || '-')} />
                </DataTable>
                {filteredRows.length ? (
                    <div className="mt-3 text-sm text-600">
                        {t('Totals')}: {formatMoney(filteredTotal)} | {formatCount(filteredRows.length)} {t('open bills')}
                    </div>
                ) : null}
            </>
        ) : null}
    </Card>
);

const ConnectionBadge = ({ connection, businessUnit, t }) => {
    if (!businessUnit || businessUnit === 'consolidated') {
        return <Tag value={t('Select unit')} severity="info" />;
    }
    if (!connection?.connected) {
        return <Tag value={t('Disconnected')} severity="danger" />;
    }
    if (connection?.health === 'warning' || connection?.lastRefreshError) {
        return <Tag value={t('Review connection')} severity="warning" />;
    }
    return <Tag value={t('Connected')} severity="success" />;
};

const QuickBooksModule = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const adminMenuRef = useRef(null);
    const commercialRequestSequenceRef = useRef(0);
    const vendorBillPaymentsRequestSequenceRef = useRef(0);
    const vendorBillPaymentsTableRef = useRef(null);
    const preferredLanguage = usePreferredLanguage();
    const t = useCallback((value) => translateUiText(value, preferredLanguage), [preferredLanguage]);
    const tokenT = useCallback((value) => translateUiToken(value, preferredLanguage), [preferredLanguage]);
    const defaultCommercialRange = useMemo(() => getDefaultCommercialRange(), []);
    const currentUser = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const currentAdmin = currentUser?.data?.adminUser || {};
    const currentRoleUid = currentAdmin?.role?.uid;
    const currentEmail = String(currentAdmin?.email || '').trim().toLowerCase();
    const isAdminUser = currentRoleUid === 'admin' || currentRoleUid === 'superAdmin';
    const quickBooksViewAccess = getQuickBooksViewAccess(currentRoleUid, currentEmail);
    const canReadAllQuickBooks = isAdminUser || quickBooksViewAccess.fullRead;
    const isReadOnlyQuickBooksUser = quickBooksViewAccess.readOnly;
    const canViewQuickBooksManagementDashboard = quickBooksViewAccess.management;
    const isManagementOnlyUser = quickBooksViewAccess.managementOnly;
    const isFinanceDashboardUser = quickBooksViewAccess.financeOnly;
    const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(isFinanceDashboardUser ? 'empaque' : 'consolidated');
    const [activeTeam, setActiveTeam] = useState(isManagementOnlyUser ? 'gerencia' : isFinanceDashboardUser ? 'finanzas' : 'comercial');
    const [showDataPanel, setShowDataPanel] = useState(false);
    const [quotaDialogVisible, setQuotaDialogVisible] = useState(false);
    const [customerQuotaDialogVisible, setCustomerQuotaDialogVisible] = useState(false);
    const [commercialFromDate, setCommercialFromDate] = useState(defaultCommercialRange.start);
    const [commercialToDate, setCommercialToDate] = useState(defaultCommercialRange.end);
    const [selectedCommercialSeller, setSelectedCommercialSeller] = useState('');
    const [commercialSellerOptions, setCommercialSellerOptions] = useState([]);
    const [showAllCommercialCustomers, setShowAllCommercialCustomers] = useState(false);
    const [sellerSalesCollapsed, setSellerSalesCollapsed] = useState(true);
    const [trendCollapsed, setTrendCollapsed] = useState(true);
    const [topCustomersCollapsed, setTopCustomersCollapsed] = useState(true);
    const [productMixCollapsed, setProductMixCollapsed] = useState(true);
    const [receivablesAgingCollapsed, setReceivablesAgingCollapsed] = useState(true);
    const [payablesAgingCollapsed, setPayablesAgingCollapsed] = useState(true);
    const [cashConversionCycleCollapsed, setCashConversionCycleCollapsed] = useState(false);
    const [accrualCashCollapsed, setAccrualCashCollapsed] = useState(false);
    const [quotaPaceCollapsed, setQuotaPaceCollapsed] = useState(true);
    const [crossSellingCollapsed, setCrossSellingCollapsed] = useState(true);
    const [repurchaseRiskCollapsed, setRepurchaseRiskCollapsed] = useState(true);
    const [receivablesAgingBucketFilter, setReceivablesAgingBucketFilter] = useState('');
    const [payablesAgingBucketFilter, setPayablesAgingBucketFilter] = useState('');
    const [crossSellingView, setCrossSellingView] = useState('detected');
    const [crossSellingStatusFilter, setCrossSellingStatusFilter] = useState('');
    const [crossSellingCategoryFilter, setCrossSellingCategoryFilter] = useState('');
    const [opportunityPriorityFilter, setOpportunityPriorityFilter] = useState('');
    const [repurchaseImportanceFilter, setRepurchaseImportanceFilter] = useState('');
    const [expandedOpportunityRows, setExpandedOpportunityRows] = useState({});
    const [quotaPacePeriod, setQuotaPacePeriod] = useState('monthly');
    const [commercialLoading, setCommercialLoading] = useState(false);
    const [commercialDashboard, setCommercialDashboard] = useState(null);
    const [savingReceivableReviewKey, setSavingReceivableReviewKey] = useState(null);
    const [receivableReviewDialogRow, setReceivableReviewDialogRow] = useState(null);
    const [receivableReviewDraft, setReceivableReviewDraft] = useState({
        reviewedAmount: 0,
        paymentProbability: 0,
        projectedCollectionDate: null
    });
    const [savingPayableAnnotationKey, setSavingPayableAnnotationKey] = useState(null);
    const [payableAnnotationDialogRow, setPayableAnnotationDialogRow] = useState(null);
    const [payableAnnotationDraft, setPayableAnnotationDraft] = useState({
        status: 'pending',
        partialPaidAmount: 0
    });
    const [cashFlowSettingsVisible, setCashFlowSettingsVisible] = useState(false);
    const [cashFlowTableCollapsed, setCashFlowTableCollapsed] = useState(false);
    const [expandedCashFlowRows, setExpandedCashFlowRows] = useState({});
    const [cashFlowMinimumLiquidity, setCashFlowMinimumLiquidity] = useState(1000);
    const [cashFlowCollectionProbabilities, setCashFlowCollectionProbabilities] = useState(DEFAULT_CASH_FLOW_COLLECTION_PROBABILITIES.empaque);
    const [cashFlowManualCollections, setCashFlowManualCollections] = useState(createEmptyWeeklyManualValues);
    const [cashFlowManualPayments, setCashFlowManualPayments] = useState(createEmptyWeeklyManualValues);
    const [cashFlowVehicleMaintenanceReserve, setCashFlowVehicleMaintenanceReserve] = useState(0);
    const [cashFlowVendorPaymentDays, setCashFlowVendorPaymentDays] = useState([10, 25]);
    const [cashFlowFixedExpenseDay, setCashFlowFixedExpenseDay] = useState(31);
    const [appliedCashFlowAssumptions, setAppliedCashFlowAssumptions] = useState({
        minimumLiquidity: 1000,
        collectionProbabilities: DEFAULT_CASH_FLOW_COLLECTION_PROBABILITIES.empaque,
        manualCollections: createEmptyWeeklyManualValues(),
        manualPayments: createEmptyWeeklyManualValues(),
        vehicleMaintenanceReserve: 0,
        vendorPaymentDays: [10, 25],
        fixedExpenseDay: 31
    });
    const [vendingReconciliationLoading, setVendingReconciliationLoading] = useState(false);
    const [vendingReconciliation, setVendingReconciliation] = useState(null);
    const [vendorBillPaymentsLoading, setVendorBillPaymentsLoading] = useState(false);
    const [vendorBillPayments, setVendorBillPayments] = useState(null);
    const [vendorBillPaymentSearch, setVendorBillPaymentSearch] = useState('');
    const [vendorBillPaymentAccountFilter, setVendorBillPaymentAccountFilter] = useState('');
    const [quotaYear, setQuotaYear] = useState(new Date().getFullYear());
    const [quotaQuarter, setQuotaQuarter] = useState(getCurrentQuarter());
    const [customerQuotaYear, setCustomerQuotaYear] = useState(new Date().getFullYear());
    const [customerQuotaQuarter, setCustomerQuotaQuarter] = useState(getCurrentQuarter());
    const [quarterQuotaLoading, setQuarterQuotaLoading] = useState(false);
    const [quarterQuotaSaving, setQuarterQuotaSaving] = useState(false);
    const [quarterQuotaMeta, setQuarterQuotaMeta] = useState(null);
    const [quarterQuotaRows, setQuarterQuotaRows] = useState([]);
    const [customerQuotaLoading, setCustomerQuotaLoading] = useState(false);
    const [customerQuotaMeta, setCustomerQuotaMeta] = useState(null);
    const [customerQuotaRows, setCustomerQuotaRows] = useState([]);
    const [customerQuotaSaving, setCustomerQuotaSaving] = useState(false);
    const [statusRows, setStatusRows] = useState([]);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [syncSummary, setSyncSummary] = useState(null);
    const [syncLogs, setSyncLogs] = useState([]);
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [audit, setAudit] = useState(null);
    const [managementCycle, setManagementCycle] = useState(null);
    const [managementCycleLoaded, setManagementCycleLoaded] = useState(false);

    const selectedStatus = useMemo(() => {
        if (selectedBusinessUnit === 'consolidated') return null;
        return statusRows.find((row) => row.key === selectedBusinessUnit) || null;
    }, [selectedBusinessUnit, statusRows]);

    const selectedConnection = selectedStatus?.connection || null;
    const commercialDisabled = selectedBusinessUnit === 'consolidated';
    const dateFilterInvalid = commercialFromDate && commercialToDate && commercialFromDate > commercialToDate;
    const dashboardTeamUsesQuickBooksData = activeTeam === 'comercial' || activeTeam === 'finanzas';
    const businessUnitOptions = useMemo(() => BUSINESS_UNIT_OPTIONS.map((option) => ({ ...option, label: t(option.label) })), [t]);
    const teamOptions = useMemo(
        () =>
            TEAM_OPTIONS.filter((option) => {
                if (isManagementOnlyUser) return option.value === 'gerencia';
                if (option.value === 'gerencia') return canViewQuickBooksManagementDashboard;
                return true;
            }).map((option) => ({
                ...option,
                label: t(option.label)
            })),
        [canViewQuickBooksManagementDashboard, isManagementOnlyUser, t]
    );

    useEffect(() => {
        if (!canViewQuickBooksManagementDashboard || activeTeam !== 'gerencia' || managementCycleLoaded) return undefined;

        let active = true;
        const loadManagementCycle = async () => {
            const response = await handleGetRequest(endPoints.okrCycles, true);
            if (!active) return;
            const cycles = Array.isArray(response?.data) ? response.data : [];
            setManagementCycle(cycles.find((cycle) => cycle.status === 'active') || cycles[0] || null);
            setManagementCycleLoaded(true);
        };

        loadManagementCycle();
        return () => {
            active = false;
        };
    }, [activeTeam, canViewQuickBooksManagementDashboard, managementCycleLoaded]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const result = searchParams.get('quickbooks');
        const message = searchParams.get('message');
        const businessUnit = searchParams.get('businessUnit');
        if (!result) return;

        if (businessUnit) {
            setSelectedBusinessUnit(businessUnit);
        }

        if (result === 'connected') {
            toast.success(t('QuickBooks connected successfully'));
        } else if (result === 'error') {
            toast.error(message || t('QuickBooks connection failed'));
        }

        searchParams.delete('quickbooks');
        searchParams.delete('message');
        searchParams.delete('businessUnit');
        const nextQuery = searchParams.toString();
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
        window.history.replaceState({}, '', nextUrl);
    }, [location.search, t]);

    useEffect(() => {
        const defaults = DEFAULT_CASH_FLOW_COLLECTION_PROBABILITIES[selectedBusinessUnit] || DEFAULT_CASH_FLOW_COLLECTION_PROBABILITIES.empaque;
        setCashFlowCollectionProbabilities(defaults);
        setAppliedCashFlowAssumptions((current) => ({
            ...current,
            collectionProbabilities: defaults
        }));
    }, [selectedBusinessUnit]);

    const fetchStatuses = useCallback(async () => {
        const response = await handleGetRequest(endPoints.quickbooksStatus('businessUnit=consolidated'), false, false, dispatch);
        const rows = Array.isArray(response?.data) ? response.data : [];
        setStatusRows(rows);
        return rows;
    }, [dispatch]);

    const fetchBusinessUnitDetails = useCallback(
        async (businessUnit) => {
            if (!businessUnit || businessUnit === 'consolidated') {
                setCompanyInfo(null);
                setSyncSummary(null);
                setSyncLogs([]);
                setEntities([]);
                return;
            }

            const unitParam = `businessUnit=${encodeURIComponent(businessUnit)}`;
            const [companyRes, summaryRes, logsRes, entitiesRes] = await Promise.all([
                handleGetRequest(endPoints.quickbooksCompanyInfo(unitParam), false, false, dispatch),
                handleGetRequest(endPoints.quickbooksSyncSummary(unitParam), false, false, dispatch),
                handleGetRequest(endPoints.quickbooksSyncLogs(`${unitParam}&limit=10`), false, false, dispatch),
                handleGetRequest(endPoints.quickbooksEntities(`${unitParam}&limit=25`), false, false, dispatch)
            ]);

            setCompanyInfo(companyRes?.data || null);
            setSyncSummary(summaryRes?.data || null);
            setSyncLogs(Array.isArray(logsRes?.data) ? logsRes.data : []);
            setEntities(Array.isArray(entitiesRes?.data) ? entitiesRes.data : []);
        },
        [dispatch]
    );

    const fetchCommercialDashboard = useCallback(async () => {
        const requestSequence = commercialRequestSequenceRef.current + 1;
        commercialRequestSequenceRef.current = requestSequence;
        if (commercialDisabled) {
            setCommercialDashboard(null);
            setCommercialLoading(false);
            return;
        }

        if (!(commercialFromDate instanceof Date) || Number.isNaN(commercialFromDate.getTime())) {
            setCommercialLoading(false);
            return;
        }
        if (!(commercialToDate instanceof Date) || Number.isNaN(commercialToDate.getTime())) {
            setCommercialLoading(false);
            return;
        }
        if (commercialFromDate > commercialToDate) {
            setCommercialDashboard(null);
            setCommercialLoading(false);
            return;
        }

        const cashFlowOverrides = {
            minimumLiquidity: appliedCashFlowAssumptions.minimumLiquidity,
            vehicleMaintenanceReserve: appliedCashFlowAssumptions.vehicleMaintenanceReserve,
            vendorPaymentDays: appliedCashFlowAssumptions.vendorPaymentDays,
            fixedExpenseDay: appliedCashFlowAssumptions.fixedExpenseDay,
            collectionProbabilities: appliedCashFlowAssumptions.collectionProbabilities,
            manualCollections: appliedCashFlowAssumptions.manualCollections,
            manualPayments: appliedCashFlowAssumptions.manualPayments
        };
        const params = buildCommercialParams(
            selectedBusinessUnit,
            commercialFromDate,
            commercialToDate,
            selectedCommercialSeller,
            cashFlowOverrides
        );
        setCommercialLoading(true);
        try {
            const response = await handleGetRequest(endPoints.quickbooksCommercialDashboard(params), false, false, dispatch);
            if (requestSequence !== commercialRequestSequenceRef.current) return;
            const data = response?.data || null;
            setCommercialDashboard(data);
            if (!selectedCommercialSeller) {
                const sellerRows = Array.isArray(data?.sellers?.rows) ? data.sellers.rows : [];
                const nextSellerOptions = sellerRows
                    .map((row) => row?.seller)
                    .filter(Boolean)
                    .sort((a, b) => String(a).localeCompare(String(b)));
                setCommercialSellerOptions(nextSellerOptions);
            }
        } finally {
            if (requestSequence === commercialRequestSequenceRef.current) {
                setCommercialLoading(false);
            }
        }
    }, [
        appliedCashFlowAssumptions,
        commercialDisabled,
        commercialFromDate,
        commercialToDate,
        dispatch,
        selectedBusinessUnit,
        selectedCommercialSeller
    ]);

    const fetchVendingReconciliation = useCallback(async () => {
        if (activeTeam !== 'finanzas' || selectedBusinessUnit !== 'coffee_vending' || dateFilterInvalid) {
            setVendingReconciliation(null);
            return;
        }
        if (!(commercialFromDate instanceof Date) || Number.isNaN(commercialFromDate.getTime())) return;
        if (!(commercialToDate instanceof Date) || Number.isNaN(commercialToDate.getTime())) return;

        const params = new URLSearchParams();
        params.set('businessUnit', selectedBusinessUnit);
        const startDate = toDateParam(commercialFromDate);
        const endDate = toDateParam(commercialToDate, true);
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);

        setVendingReconciliationLoading(true);
        try {
            const response = await handleGetRequest(endPoints.quickbooksVendingReconciliation(params.toString()), false, false, dispatch);
            setVendingReconciliation(response?.data || null);
        } finally {
            setVendingReconciliationLoading(false);
        }
    }, [activeTeam, commercialFromDate, commercialToDate, dateFilterInvalid, dispatch, selectedBusinessUnit]);

    const fetchVendorBillPayments = useCallback(async () => {
        const requestSequence = vendorBillPaymentsRequestSequenceRef.current + 1;
        vendorBillPaymentsRequestSequenceRef.current = requestSequence;
        if (activeTeam !== 'finanzas' || commercialDisabled || dateFilterInvalid) {
            setVendorBillPayments(null);
            setVendorBillPaymentsLoading(false);
            return;
        }
        if (!(commercialFromDate instanceof Date) || Number.isNaN(commercialFromDate.getTime())) return;
        if (!(commercialToDate instanceof Date) || Number.isNaN(commercialToDate.getTime())) return;

        const params = new URLSearchParams();
        params.set('businessUnit', selectedBusinessUnit);
        const startDate = toDateParam(commercialFromDate);
        const endDate = toDateParam(commercialToDate, true);
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);

        setVendorBillPaymentsLoading(true);
        try {
            const response = await handleGetRequest(endPoints.quickbooksVendorBillPayments(params.toString()), false, false, dispatch);
            if (requestSequence !== vendorBillPaymentsRequestSequenceRef.current) return;
            setVendorBillPayments(
                response?.data || {
                    error: t('No se pudo cargar la lista de pagos de proveedores.'),
                    rows: [],
                    totals: {}
                }
            );
        } finally {
            if (requestSequence === vendorBillPaymentsRequestSequenceRef.current) {
                setVendorBillPaymentsLoading(false);
            }
        }
    }, [
        activeTeam,
        commercialDisabled,
        commercialFromDate,
        commercialToDate,
        dateFilterInvalid,
        dispatch,
        selectedBusinessUnit,
        t
    ]);

    const handleReceivableReviewChange = useCallback(
        async (row, patch) => {
            if (!row?.quickBooksInvoiceId) return;
            const reviewedAmount = Math.min(Number(patch.reviewedAmount ?? row.reviewedAmount ?? row.amount ?? 0), Number(row.amount || 0));
            const paymentProbability = Math.min(Math.max(Number(patch.paymentProbability ?? row.paymentProbability ?? 0), 0), 100);
            const projectedCollectionDate = toDateParam(patch.projectedCollectionDate || parseDateOnly(row.projectedCollectionDate));
            const payload = {
                businessUnit: selectedBusinessUnit,
                quickBooksInvoiceId: row.quickBooksInvoiceId,
                reviewedAmount,
                paymentProbability,
                projectedCollectionDate
            };

            setSavingReceivableReviewKey(row.quickBooksInvoiceId);
            setCommercialDashboard((current) => {
                if (!current?.receivablesAging?.rows) return current;
                return {
                    ...current,
                    receivablesAging: {
                        ...current.receivablesAging,
                        rows: current.receivablesAging.rows.map((item) =>
                            item.quickBooksInvoiceId === row.quickBooksInvoiceId
                                ? {
                                      ...item,
                                      hasReceivableReview: true,
                                      reviewedAmount,
                                      paymentProbability,
                                      projectedCollectionDate,
                                      expectedPaymentAmount: reviewedAmount * (paymentProbability / 100)
                                  }
                                : item
                        )
                    }
                };
            });

            try {
                await dispatch(handlePostRequest(payload, endPoints.quickbooksReceivableInvoiceAnnotation, false, false));
                await fetchCommercialDashboard();
            } finally {
                setSavingReceivableReviewKey(null);
            }
        },
        [dispatch, fetchCommercialDashboard, selectedBusinessUnit]
    );

    const handleOpenReceivableReviewDialog = useCallback((row) => {
        setReceivableReviewDialogRow(row);
        setReceivableReviewDraft({
            reviewedAmount: Number(row?.reviewedAmount ?? row?.amount ?? 0),
            paymentProbability: Number(row?.paymentProbability ?? 0),
            projectedCollectionDate: parseDateOnly(row?.projectedCollectionDate)
        });
    }, []);

    const handleCloseReceivableReviewDialog = useCallback(() => {
        setReceivableReviewDialogRow(null);
        setReceivableReviewDraft({
            reviewedAmount: 0,
            paymentProbability: 0,
            projectedCollectionDate: null
        });
    }, []);

    const handleSaveReceivableReviewDialog = useCallback(async () => {
        if (!receivableReviewDialogRow) return;
        await handleReceivableReviewChange(receivableReviewDialogRow, receivableReviewDraft);
        handleCloseReceivableReviewDialog();
    }, [handleCloseReceivableReviewDialog, handleReceivableReviewChange, receivableReviewDialogRow, receivableReviewDraft]);

    const handlePayableAnnotationChange = useCallback(
        async (row, patch) => {
            if (!row?.quickBooksBillId) return;
            const nextStatus = patch.status || row.paymentStatus || 'pending';
            const nextPartialPaidAmount =
                nextStatus === 'partial_paid'
                    ? Math.min(Number(patch.partialPaidAmount ?? row.partialPaidAmount ?? 0), Number(row.amount || 0))
                    : 0;
            const payload = {
                businessUnit: selectedBusinessUnit,
                quickBooksBillId: row.quickBooksBillId,
                status: nextStatus,
                partialPaidAmount: nextPartialPaidAmount
            };

            setSavingPayableAnnotationKey(row.quickBooksBillId);
            setCommercialDashboard((current) => {
                if (!current?.payablesAging?.rows) return current;
                return {
                    ...current,
                    payablesAging: {
                        ...current.payablesAging,
                        rows: current.payablesAging.rows.map((item) =>
                            item.quickBooksBillId === row.quickBooksBillId
                                ? {
                                      ...item,
                                      paymentStatus: payload.status,
                                      partialPaidAmount: payload.partialPaidAmount,
                                      estimatedRemainingAmount: Math.max(Number(item.amount || 0) - payload.partialPaidAmount, 0)
                                  }
                                : item
                        )
                    }
                };
            });

            try {
                await dispatch(handlePostRequest(payload, endPoints.quickbooksPayableBillAnnotation, false, false));
            } finally {
                setSavingPayableAnnotationKey(null);
            }
        },
        [dispatch, selectedBusinessUnit]
    );

    const handleOpenPayableAnnotationDialog = useCallback((row) => {
        setPayableAnnotationDialogRow(row);
        setPayableAnnotationDraft({
            status: row?.paymentStatus || 'pending',
            partialPaidAmount: Number(row?.partialPaidAmount || 0)
        });
    }, []);

    const handleClosePayableAnnotationDialog = useCallback(() => {
        setPayableAnnotationDialogRow(null);
        setPayableAnnotationDraft({
            status: 'pending',
            partialPaidAmount: 0
        });
    }, []);

    const handlePayableAnnotationDraftStatusChange = useCallback((status) => {
        setPayableAnnotationDraft((current) => ({
            status,
            partialPaidAmount: status === 'partial_paid' ? current.partialPaidAmount : 0
        }));
    }, []);

    const handleSavePayableAnnotationDialog = useCallback(async () => {
        if (!payableAnnotationDialogRow) return;
        await handlePayableAnnotationChange(payableAnnotationDialogRow, payableAnnotationDraft);
        handleClosePayableAnnotationDialog();
    }, [handleClosePayableAnnotationDialog, handlePayableAnnotationChange, payableAnnotationDialogRow, payableAnnotationDraft]);

    const fetchQuarterQuotaEditor = useCallback(async () => {
        if (!QUOTA_SUPPORTED_UNITS.has(selectedBusinessUnit)) {
            setQuarterQuotaMeta(null);
            setQuarterQuotaRows([]);
            return;
        }

        const params = buildQuarterParams(selectedBusinessUnit, quotaYear, quotaQuarter);
        setQuarterQuotaLoading(true);
        try {
            const response = await handleGetRequest(endPoints.quickbooksCommercialQuarterQuotas(params), false, false, dispatch);
            const data = response?.data || null;
            const dashboardSellers = Array.isArray(commercialDashboard?.sellerProfiles)
                ? commercialDashboard.sellerProfiles
                      .map((row) => row?.seller)
                      .filter((seller) => seller && seller !== 'Sin Vendedor')
                : [];
            const savedRows = Array.isArray(data?.sellerQuotas) ? data.sellerQuotas : [];
            const sellerNames = [...new Set([...dashboardSellers, ...savedRows.map((row) => row?.seller).filter(Boolean)])].sort((a, b) =>
                String(a).localeCompare(String(b))
            );
            const rowsBySeller = new Map(savedRows.map((row) => [row.seller, row]));
            const nextRows = sellerNames.map((seller) => {
                const savedRow = rowsBySeller.get(seller);
                const monthlyAllocations = Array.isArray(savedRow?.monthlyAllocations) && savedRow.monthlyAllocations.length === 3
                    ? savedRow.monthlyAllocations.map((value) => Number(value || 0))
                    : [0, 0, 0];
                const quarterlyQuota = Number(savedRow?.quarterlyQuota || monthlyAllocations.reduce((sum, value) => sum + Number(value || 0), 0));
                return {
                    seller,
                    quarterlyQuota,
                    monthlyAllocations
                };
            });
            setQuarterQuotaMeta(data);
            setQuarterQuotaRows(nextRows);
        } finally {
            setQuarterQuotaLoading(false);
        }
    }, [commercialDashboard?.sellerProfiles, dispatch, quotaQuarter, quotaYear, selectedBusinessUnit]);

    const fetchCustomerQuarterQuotaEditor = useCallback(async () => {
        if (!QUOTA_SUPPORTED_UNITS.has(selectedBusinessUnit)) {
            setCustomerQuotaMeta(null);
            setCustomerQuotaRows([]);
            return;
        }

        const params = buildQuarterParams(selectedBusinessUnit, customerQuotaYear, customerQuotaQuarter);
        setCustomerQuotaLoading(true);
        try {
            const response = await handleGetRequest(endPoints.quickbooksCommercialCustomerQuarterQuotas(params), false, false, dispatch);
            const data = response?.data || null;
            const dashboardRows = Array.isArray(commercialDashboard?.topCustomers) ? commercialDashboard.topCustomers : [];
            const savedRows = Array.isArray(data?.customerQuotas) ? data.customerQuotas : [];
            const customerNames = [...new Set([...dashboardRows.map((row) => row?.name).filter(Boolean), ...savedRows.map((row) => row?.customer).filter(Boolean)])].sort(
                (a, b) => String(a).localeCompare(String(b))
            );
            const rowsByCustomer = new Map(savedRows.map((row) => [row.customer, row]));
            const dashboardByCustomer = new Map(dashboardRows.map((row) => [row.name, row]));
            const nextRows = customerNames.map((customer) => {
                const savedRow = rowsByCustomer.get(customer);
                const dashboardRow = dashboardByCustomer.get(customer);
                const monthlyAllocations = Array.isArray(savedRow?.monthlyAllocations) && savedRow.monthlyAllocations.length === 3
                    ? savedRow.monthlyAllocations.map((value) => Number(value || 0))
                    : [0, 0, 0];
                const quarterlyQuota = Number(savedRow?.quarterlyQuota || monthlyAllocations.reduce((sum, value) => sum + Number(value || 0), 0));
                return {
                    customer,
                    netSales: Number(dashboardRow?.total || 0),
                    quarterlyQuota,
                    monthlyAllocations,
                    attainment: quarterlyQuota ? Number(((Number(dashboardRow?.total || 0) / quarterlyQuota) * 100).toFixed(1)) : null
                };
            });
            setCustomerQuotaMeta(data);
            setCustomerQuotaRows(nextRows);
        } finally {
            setCustomerQuotaLoading(false);
        }
    }, [commercialDashboard?.topCustomers, customerQuotaQuarter, customerQuotaYear, dispatch, selectedBusinessUnit]);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        if (isManagementOnlyUser) {
            setStatusRows([]);
            setAudit(null);
            setCompanyInfo(null);
            setSyncSummary(null);
            setSyncLogs([]);
            setEntities([]);
            setShowDataPanel(false);
            setLoading(false);
            return;
        }
        await fetchStatuses();
        if (isFinanceDashboardUser) {
            setAudit(null);
            setCompanyInfo(null);
            setSyncSummary(null);
            setSyncLogs([]);
            setEntities([]);
            setShowDataPanel(false);
            setLoading(false);
            return;
        }
        const auditResponse = await handleGetRequest(endPoints.quickbooksAudit, false, false, dispatch);
        setAudit(auditResponse?.data || null);
        await fetchBusinessUnitDetails(selectedBusinessUnit);
        setLoading(false);
    }, [dispatch, fetchBusinessUnitDetails, fetchStatuses, isFinanceDashboardUser, isManagementOnlyUser, selectedBusinessUnit]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    useEffect(() => {
        if (!dashboardTeamUsesQuickBooksData) return;
        fetchCommercialDashboard();
    }, [dashboardTeamUsesQuickBooksData, fetchCommercialDashboard]);

    useEffect(() => {
        fetchVendingReconciliation();
    }, [fetchVendingReconciliation]);

    useEffect(() => {
        fetchVendorBillPayments();
    }, [fetchVendorBillPayments]);

    useEffect(() => {
        setVendorBillPaymentSearch('');
        setVendorBillPaymentAccountFilter('');
    }, [selectedBusinessUnit]);

    useEffect(() => {
        if (!quotaDialogVisible) return;
        fetchQuarterQuotaEditor();
    }, [fetchQuarterQuotaEditor, quotaDialogVisible]);

    useEffect(() => {
        if (!customerQuotaDialogVisible) return;
        fetchCustomerQuarterQuotaEditor();
    }, [customerQuotaDialogVisible, fetchCustomerQuarterQuotaEditor]);

    const handleConnect = useCallback((businessUnit) => {
        const params = new URLSearchParams({
            businessUnit,
            returnTo: `${window.location.origin}${window.location.pathname}`
        });
        window.location.assign(`${baseURL}${endPoints.quickbooksConnect(params.toString())}`);
    }, []);

    const handleRunSync = async () => {
        if (!selectedBusinessUnit || selectedBusinessUnit === 'consolidated') {
            toast.warn(t('Select a business unit first'));
            return;
        }
        setSyncing(true);
        const payload = {
            businessUnit: selectedBusinessUnit,
            full: true,
            entityTypes: ['customers', 'invoices', 'creditMemos', 'payments', 'bills'],
            maxPages: 50,
            pageSize: 100
        };
        const response = await dispatch(handlePostRequest(payload, endPoints.quickbooksRunBusinessUnitSync, false, true));
        if (response?.status === 200) {
            await Promise.all([refreshAll(), fetchCommercialDashboard(), fetchVendorBillPayments()]);
        }
        setSyncing(false);
    };

    const handleDisconnect = async () => {
        if (!selectedBusinessUnit || selectedBusinessUnit === 'consolidated') {
            toast.warn(t('Select a business unit first'));
            return;
        }
        setDisconnecting(true);
        const response = await dispatch(
            handlePostRequest({}, endPoints.quickbooksDisconnect(`businessUnit=${encodeURIComponent(selectedBusinessUnit)}`), false, true)
        );
        if (response?.status === 200) {
            await refreshAll();
            setCommercialDashboard(null);
            setVendorBillPayments(null);
        }
        setDisconnecting(false);
    };

    const handleRefreshAll = async () => {
        await Promise.all([
            refreshAll(),
            dashboardTeamUsesQuickBooksData ? fetchCommercialDashboard() : Promise.resolve(),
            activeTeam === 'finanzas' ? fetchVendorBillPayments() : Promise.resolve()
        ]);
    };

    const handleCashFlowProbabilityChange = (bucket, value) => {
        setCashFlowCollectionProbabilities((current) => ({
            ...current,
            [bucket]: Number(value || 0)
        }));
    };

    const handleCashFlowWeeklyValueChange = (setter, weekIndex, value) => {
        setter((current) => current.map((entry, index) => (index === weekIndex ? Number(value || 0) : entry)));
    };

    const handleCashFlowVendorPaymentDayChange = (dayIndex, value) => {
        setCashFlowVendorPaymentDays((current) => current.map((entry, index) => (index === dayIndex ? Number(value || 1) : entry)));
    };

    const handleApplyCashFlowAssumptions = () => {
        setAppliedCashFlowAssumptions({
            minimumLiquidity: cashFlowMinimumLiquidity,
            collectionProbabilities: cashFlowCollectionProbabilities,
            manualCollections: cashFlowManualCollections,
            manualPayments: cashFlowManualPayments,
            vehicleMaintenanceReserve: cashFlowVehicleMaintenanceReserve,
            vendorPaymentDays: cashFlowVendorPaymentDays,
            fixedExpenseDay: cashFlowFixedExpenseDay
        });
        setCashFlowSettingsVisible(false);
    };

    const handleResetCashFlowAssumptions = () => {
        const defaults = DEFAULT_CASH_FLOW_COLLECTION_PROBABILITIES[selectedBusinessUnit] || DEFAULT_CASH_FLOW_COLLECTION_PROBABILITIES.empaque;
        const emptyCollections = createEmptyWeeklyManualValues();
        const emptyPayments = createEmptyWeeklyManualValues();
        setCashFlowMinimumLiquidity(1000);
        setCashFlowCollectionProbabilities(defaults);
        setCashFlowManualCollections(emptyCollections);
        setCashFlowManualPayments(emptyPayments);
        setCashFlowVehicleMaintenanceReserve(0);
        setCashFlowVendorPaymentDays([10, 25]);
        setCashFlowFixedExpenseDay(31);
        setAppliedCashFlowAssumptions({
            minimumLiquidity: 1000,
            collectionProbabilities: defaults,
            manualCollections: emptyCollections,
            manualPayments: emptyPayments,
            vehicleMaintenanceReserve: 0,
            vendorPaymentDays: [10, 25],
            fixedExpenseDay: 31
        });
    };

    const handleQuarterQuotaChange = (seller, field, value, monthIndex = null) => {
        setQuarterQuotaRows((current) =>
            current.map((row) => {
                if (row.seller !== seller) return row;
                if (field === 'quarterlyQuota') {
                    const quarterlyQuota = Number(value || 0);
                    return {
                        ...row,
                        quarterlyQuota,
                        monthlyAllocations: splitQuarterlyQuotaEvenly(quarterlyQuota)
                    };
                }
                if (field === 'monthlyAllocation' && monthIndex != null) {
                    const monthlyAllocations = row.monthlyAllocations.map((entry, index) =>
                        index === monthIndex ? Number(value || 0) : Number(entry || 0)
                    );
                    return {
                        ...row,
                        monthlyAllocations,
                        quarterlyQuota: Number(monthlyAllocations.reduce((sum, entry) => sum + Number(entry || 0), 0).toFixed(2))
                    };
                }
                return row;
            })
        );
    };

    const handleSaveQuarterQuotas = async () => {
        if (!QUOTA_SUPPORTED_UNITS.has(selectedBusinessUnit)) return;
        setQuarterQuotaSaving(true);
        try {
            const payload = {
                businessUnit: selectedBusinessUnit,
                year: quotaYear,
                quarter: quotaQuarter,
                sellerQuotas: quarterQuotaRows.map((row) => ({
                    seller: row.seller,
                    quarterlyQuota: row.quarterlyQuota,
                    monthlyAllocations: row.monthlyAllocations
                }))
            };
            const response = await dispatch(handlePostRequest(payload, endPoints.quickbooksCommercialQuarterQuotas(), false, true));
            if (response?.status === 200) {
                await Promise.all([fetchQuarterQuotaEditor(), fetchCommercialDashboard()]);
            }
        } finally {
            setQuarterQuotaSaving(false);
        }
    };

    const openCustomerQuotaDialog = () => {
        const dashboardPeriodStart = parseDashboardDate(commercialDashboard?.period?.startDate || commercialDashboard?.period?.startDateText);
        if (dashboardPeriodStart) {
            setCustomerQuotaYear(dashboardPeriodStart.getFullYear());
            setCustomerQuotaQuarter(Math.floor(dashboardPeriodStart.getMonth() / 3) + 1);
        }
        setCustomerQuotaDialogVisible(true);
    };

    const handleCustomerQuotaChange = (customer, field, value, monthIndex = null) => {
        setCustomerQuotaRows((current) =>
            current.map((row) => {
                if (row.customer !== customer) return row;
                if (field === 'quarterlyQuota') {
                    const quarterlyQuota = Number(value || 0);
                    return {
                        ...row,
                        quarterlyQuota,
                        monthlyAllocations: splitQuarterlyQuotaEvenly(quarterlyQuota),
                        attainment: quarterlyQuota ? Number(((Number(row.netSales || 0) / quarterlyQuota) * 100).toFixed(1)) : null
                    };
                }
                if (field === 'monthlyAllocation' && monthIndex != null) {
                    const monthlyAllocations = row.monthlyAllocations.map((entry, index) =>
                        index === monthIndex ? Number(value || 0) : Number(entry || 0)
                    );
                    const quarterlyQuota = Number(monthlyAllocations.reduce((sum, entry) => sum + Number(entry || 0), 0).toFixed(2));
                    return {
                        ...row,
                        monthlyAllocations,
                        quarterlyQuota,
                        attainment: quarterlyQuota ? Number(((Number(row.netSales || 0) / quarterlyQuota) * 100).toFixed(1)) : null
                    };
                }
                return row;
            })
        );
    };

    const handleSaveCustomerQuotas = async () => {
        if (!QUOTA_SUPPORTED_UNITS.has(selectedBusinessUnit)) return;
        setCustomerQuotaSaving(true);
        try {
            const payload = {
                businessUnit: selectedBusinessUnit,
                year: customerQuotaYear,
                quarter: customerQuotaQuarter,
                customerQuotas: customerQuotaRows
                    .map((row) => ({
                        customer: row.customer,
                        quarterlyQuota: row.quarterlyQuota,
                        monthlyAllocations: row.monthlyAllocations
                    }))
                    .filter((row) => row.customer && Number(row.quarterlyQuota || 0) > 0)
            };
            const response = await dispatch(handlePostRequest(payload, endPoints.quickbooksCommercialCustomerQuarterQuotas(), false, true));
            if (response?.status === 200) {
                await Promise.all([fetchCustomerQuarterQuotaEditor(), fetchCommercialDashboard()]);
            }
        } finally {
            setCustomerQuotaSaving(false);
        }
    };

    const handleApplyEvenSplit = () => {
        setQuarterQuotaRows((current) =>
            current.map((row) => ({
                ...row,
                monthlyAllocations: splitQuarterlyQuotaEvenly(row.quarterlyQuota)
            }))
        );
    };

    const handleApplyCustomerEvenSplit = () => {
        setCustomerQuotaRows((current) =>
            current.map((row) => ({
                ...row,
                monthlyAllocations: splitQuarterlyQuotaEvenly(row.quarterlyQuota)
            }))
        );
    };

    const companyDetails = companyInfo?.CompanyInfo || companyInfo || {};
    const summaryCounts = Array.isArray(syncSummary?.counts) ? syncSummary.counts : [];
    const duplicateRealmWarnings = Array.isArray(audit?.duplicateRealmAssignments) ? audit.duplicateRealmAssignments : [];
    const commercialMetrics = commercialDashboard?.metrics || {};
    const commercialSellerRows = useMemo(
        () => (Array.isArray(commercialDashboard?.sellers?.rows) ? commercialDashboard.sellers.rows : []),
        [commercialDashboard?.sellers?.rows]
    );
    const sellersWithQuota = commercialSellerRows.filter((row) => Number(row?.quota || 0) > 0);
    const sellersAtNinetyPercentQuota = sellersWithQuota.filter((row) => Number(row?.attainment || 0) >= 90);
    const sellerQuotaProgressTemplate = (rowData) => {
        const quota = Number(rowData?.quota || 0);
        const netSales = Number(rowData?.netSales || 0);
        if (!quota) return <span className="text-600">{t('No quota')}</span>;

        const attainment = Number.isFinite(Number(rowData?.attainment)) ? Number(rowData.attainment) : (netSales / quota) * 100;
        const progress = Math.min(Math.max(attainment, 0), 100);
        const progressColor = attainment >= 100 ? '#10b981' : attainment >= 90 ? '#2563eb' : '#f59e0b';

        return (
            <div style={{ minWidth: '190px' }}>
                <div className="flex justify-content-between align-items-center mb-2 text-sm">
                    <span className="font-medium text-900">{formatPercent(attainment)}</span>
                    <span className="text-600">
                        {formatMoney(netSales)} / {formatMoney(quota)}
                    </span>
                </div>
                <div className="surface-200 border-round overflow-hidden" style={{ height: '0.65rem' }}>
                    <div
                        className="h-full border-round"
                        style={{
                            width: `${progress}%`,
                            backgroundColor: progressColor
                        }}
                    />
                </div>
            </div>
        );
    };
    const quotaPaceRows = useMemo(
        () => buildQuotaPaceRows(commercialSellerRows, commercialDashboard?.period || {}, quotaPacePeriod),
        [commercialDashboard?.period, commercialSellerRows, quotaPacePeriod]
    );
    const quotaPaceOnTrackCount = quotaPaceRows.filter((row) => row?.severity === 'success' || Number(row?.projectedAttainment || 0) >= 90).length;
    const selectedQuotaPaceRow = selectedCommercialSeller
        ? quotaPaceRows.find((row) => row?.seller === selectedCommercialSeller)
        : null;
    const quotaPaceProjectionText = selectedQuotaPaceRow ? buildQuotaPaceProjectionText(selectedQuotaPaceRow, quotaPacePeriod) : '';
    const quotaPacePeriodOptions = useMemo(
        () => QUOTA_PACE_PERIOD_OPTIONS.map((option) => ({ ...option, label: t(option.label) })),
        [t]
    );
    const projectedCloseHeader = quotaPacePeriod === 'weekly' ? t('Projected week close') : t('Projected month close');
    const commercialCustomers = Array.isArray(commercialDashboard?.topCustomers) ? commercialDashboard.topCustomers : [];
    const visibleCommercialCustomers = showAllCommercialCustomers ? commercialCustomers : commercialCustomers.slice(0, 10);
    const productMixRows = Array.isArray(commercialDashboard?.productMix?.rows) ? commercialDashboard.productMix.rows : [];
    const crossSellingRows = useMemo(
        () => (Array.isArray(commercialDashboard?.crossSelling?.rows) ? commercialDashboard.crossSelling.rows : []),
        [commercialDashboard?.crossSelling?.rows]
    );
    const crossSellingTotals = commercialDashboard?.crossSelling?.totals || {};
    const crossSellingViewOptions = useMemo(
        () => CROSS_SELLING_VIEW_OPTIONS.map((option) => ({ ...option, label: t(option.label) })),
        [t]
    );
    const opportunityRows = useMemo(
        () =>
            (Array.isArray(commercialDashboard?.crossSelling?.opportunities?.rows) ? commercialDashboard.crossSelling.opportunities.rows : []).map((row, index) => ({
                ...row,
                opportunityKey: `${row?.customer || 'customer'}::${row?.suggestedCategory || 'category'}::${index}`
            })),
        [commercialDashboard?.crossSelling?.opportunities?.rows]
    );
    const opportunityTotals = commercialDashboard?.crossSelling?.opportunities?.totals || {};
    const crossSellingSummaryText =
        crossSellingView === 'opportunities'
            ? `${formatCount(opportunityTotals.opportunityCount)} ${t('Opportunities')} | ${formatMoney(opportunityTotals.potentialSales)} ${t('Potential sales')}`
            : `${formatCount(crossSellingTotals.customersWithNewCategories)} ${t('Customers with new categories')} | ${formatMoney(crossSellingTotals.newCategorySales)} ${t('New category sales')}`;
    const opportunityPriorityOptions = useMemo(
        () => OPPORTUNITY_PRIORITY_OPTIONS.map((option) => ({ ...option, label: t(option.label) })),
        [t]
    );
    const repurchaseRiskRows = useMemo(
        () => (Array.isArray(commercialDashboard?.repurchaseRisk?.rows) ? commercialDashboard.repurchaseRisk.rows : []),
        [commercialDashboard?.repurchaseRisk?.rows]
    );
    const repurchaseRiskTotals = commercialDashboard?.repurchaseRisk?.totals || {};
    const repurchaseRiskSummaryText = `${formatCount(repurchaseRiskTotals.atRiskCustomerCount)} ${t('Customers at risk')} | ${formatMoney(repurchaseRiskTotals.estimatedGap)} ${t('Estimated gap')} | ${formatCount(repurchaseRiskTotals.criticalCustomerCount)} ${t('Critical customers')}`;
    const repurchaseImportanceOptions = useMemo(
        () => REPURCHASE_IMPORTANCE_OPTIONS.map((option) => ({ ...option, label: t(option.label) })),
        [t]
    );
    const filteredRepurchaseRiskRows = useMemo(
        () => repurchaseRiskRows.filter((row) => !repurchaseImportanceFilter || row?.importance === repurchaseImportanceFilter),
        [repurchaseImportanceFilter, repurchaseRiskRows]
    );
    const crossSellingCategoryOptions = useMemo(() => {
        const categories = [
            ...new Set(
                [
                    ...crossSellingRows.flatMap((row) => [
                        ...(row?.currentLevel1 || []),
                        ...(row?.currentLevel2 || []),
                        ...(row?.newLevel1 || []),
                        ...(row?.newLevel2 || [])
                    ]),
                    ...opportunityRows.flatMap((row) => [row?.suggestedCategory, ...(row?.currentCategories || [])])
                ]
                    .filter(Boolean)
            )
        ].sort((a, b) => String(a).localeCompare(String(b)));
        return [{ label: t('All categories'), value: '' }, ...categories.map((category) => ({ label: category, value: category }))];
    }, [crossSellingRows, opportunityRows, t]);
    const crossSellingStatusOptions = useMemo(
        () => CROSS_SELLING_STATUS_OPTIONS.map((option) => ({ ...option, label: t(option.label) })),
        [t]
    );
    const filteredCrossSellingRows = useMemo(
        () =>
            crossSellingRows.filter((row) => {
                const matchesStatus = !crossSellingStatusFilter || row?.status === crossSellingStatusFilter;
                const categoryValues = [
                    ...(row?.currentLevel1 || []),
                    ...(row?.currentLevel2 || []),
                    ...(row?.newLevel1 || []),
                    ...(row?.newLevel2 || [])
                ];
                const matchesCategory = !crossSellingCategoryFilter || categoryValues.includes(crossSellingCategoryFilter);
                return matchesStatus && matchesCategory;
            }),
        [crossSellingCategoryFilter, crossSellingRows, crossSellingStatusFilter]
    );
    const filteredOpportunityRows = useMemo(
        () =>
            opportunityRows.filter((row) => {
                const matchesPriority = !opportunityPriorityFilter || row?.priority === opportunityPriorityFilter;
                const categoryValues = [row?.suggestedCategory, ...(row?.currentCategories || [])].filter(Boolean);
                const matchesCategory = !crossSellingCategoryFilter || categoryValues.includes(crossSellingCategoryFilter);
                return matchesPriority && matchesCategory;
            }),
        [crossSellingCategoryFilter, opportunityPriorityFilter, opportunityRows]
    );
    const isOpportunityExpandable = (rowData) => Array.isArray(rowData?.suggestedProducts) && rowData.suggestedProducts.length > 0;
    const opportunityProductExpansionTemplate = (rowData) => {
        const suggestedProducts = Array.isArray(rowData?.suggestedProducts) ? rowData.suggestedProducts : [];
        return (
            <div className="p-3 surface-50 border-round">
                <DataTable value={suggestedProducts} responsiveLayout="scroll" emptyMessage={t('No product detail available')} size="small">
                    <Column field="product" header={t('Product')} />
                    <Column field="peerSales" header={t('Peer sales')} body={(productRow) => formatMoney(productRow?.peerSales)} />
                    <Column field="similarCustomerCount" header={t('Similar customers')} body={(productRow) => formatCount(productRow?.similarCustomerCount)} />
                    <Column header={t('Customers')} body={(productRow) => formatListValue(productRow?.similarCustomers, 4)} />
                </DataTable>
            </div>
        );
    };
    const receivablesAging = useMemo(() => commercialDashboard?.receivablesAging || {}, [commercialDashboard?.receivablesAging]);
    const receivablesAgingRows = useMemo(() => (Array.isArray(receivablesAging?.rows) ? receivablesAging.rows : []), [receivablesAging]);
    const receivablesAgingSummary = useMemo(() => (Array.isArray(receivablesAging?.summary) ? receivablesAging.summary : []), [receivablesAging]);
    const payablesAging = useMemo(() => commercialDashboard?.payablesAging || {}, [commercialDashboard?.payablesAging]);
    const payablesAgingRows = useMemo(() => (Array.isArray(payablesAging?.rows) ? payablesAging.rows : []), [payablesAging]);
    const payablesAgingSummary = useMemo(() => (Array.isArray(payablesAging?.summary) ? payablesAging.summary : []), [payablesAging]);
    const filteredReceivablesAgingRows = receivablesAgingBucketFilter
        ? receivablesAgingRows.filter((row) => row?.bucket === receivablesAgingBucketFilter)
        : receivablesAgingRows;
    const filteredPayablesAgingRows = payablesAgingBucketFilter
        ? payablesAgingRows.filter((row) => row?.bucket === payablesAgingBucketFilter)
        : payablesAgingRows;
    const receivablesAgingRowsTotal = receivablesAgingRows.reduce((sum, row) => sum + Number(row?.amount || 0), 0);
    const receivablesAgingTotal = Number.isFinite(Number(receivablesAging?.total)) ? Number(receivablesAging.total) : receivablesAgingRowsTotal;
    const filteredReceivablesAgingTotal = filteredReceivablesAgingRows.reduce((sum, row) => sum + Number(row?.amount || 0), 0);
    const payablesAgingRowsTotal = payablesAgingRows.reduce((sum, row) => sum + Number(row?.amount || 0), 0);
    const payablesAgingTotal = Number.isFinite(Number(payablesAging?.total)) ? Number(payablesAging.total) : payablesAgingRowsTotal;
    const filteredPayablesAgingTotal = filteredPayablesAgingRows.reduce((sum, row) => sum + Number(row?.amount || 0), 0);
    const accountsReceivableAgingSummary = commercialDashboard?.accountsReceivableAgingSummary || {};
    const receivablesOverdueRowsTotal = receivablesAgingRows
        .filter((row) => Number(row?.days || 0) > 0)
        .reduce((sum, row) => sum + Number(row?.amount || 0), 0);
    const financeReceivablesTotal = Number.isFinite(Number(accountsReceivableAgingSummary?.totalAccountsReceivableAmount))
        ? Number(accountsReceivableAgingSummary.totalAccountsReceivableAmount)
        : receivablesAgingTotal;
    const financeOverdueReceivables = Number.isFinite(Number(accountsReceivableAgingSummary?.overdueAccountsReceivableAmount))
        ? Number(accountsReceivableAgingSummary.overdueAccountsReceivableAmount)
        : receivablesOverdueRowsTotal;
    const financeOverdueReceivablesPercentage = Number.isFinite(Number(accountsReceivableAgingSummary?.overdueAccountsReceivablePercentage))
        ? Number(accountsReceivableAgingSummary.overdueAccountsReceivablePercentage)
        : financeReceivablesTotal
            ? (financeOverdueReceivables / financeReceivablesTotal) * 100
            : null;
    const financeOverdueReceivablesHint = `${formatPercent(financeOverdueReceivablesPercentage)} ${t('of')} ${formatMoney(financeReceivablesTotal)} | ${t('Cutoff')}: ${
        accountsReceivableAgingSummary?.asOfDate || receivablesAging?.asOfDate || '-'
    }`;
    const financePayablesTotal = Number.isFinite(Number(commercialMetrics.accountsPayable))
        ? Number(commercialMetrics.accountsPayable)
        : payablesAgingTotal;
    const financePeriodLabel = commercialDashboard?.period?.label || t('Selected period');
    const financeNetSales = Number(commercialMetrics.netSales || 0);
    const financeCollected = Number(commercialMetrics.collected || 0);
    const financeCashDelta = financeCollected - financeNetSales;
    const financeCashDeltaRate = financeNetSales ? (financeCashDelta / financeNetSales) * 100 : null;
    const financeCashDeltaPercent = financeCashDeltaRate == null ? '-' : `${financeCashDeltaRate > 0 ? '+' : ''}${formatPercent(financeCashDeltaRate)}`;
    const financeCashDeltaText =
        financeCashDelta >= 0
            ? `${t('Collected exceeds net sales by')} ${formatMoney(financeCashDelta)}`
            : `${t('Collected is below net sales by')} ${formatMoney(Math.abs(financeCashDelta))}`;
    const cashConversionCycleStatus = commercialMetrics.cashConversionCycleStatus;
    const cashConversionCycleStatusLabel =
        cashConversionCycleStatus === 'success'
            ? t('Cumple')
            : cashConversionCycleStatus === 'danger'
                ? t('Fuera de meta')
                : t('No disponible');
    const cashConversionCycleHint =
        commercialMetrics.cashConversionCycle == null
            ? t('Requires DIO, DSO and DPO')
            : `${cashConversionCycleStatusLabel} | ${t('Target')} <= ${formatCount(commercialMetrics.cashConversionCycleTarget || 45)} ${t('days')}`;
    const balanceSheetMetrics = commercialMetrics?.balanceSheet || {};
    const accountsPayableStartSource = balanceSheetMetrics.accountsPayableStartLabel || t('Balance Sheet opening date');
    const accountsPayableEndSource = balanceSheetMetrics.accountsPayableEndLabel || t('Balance Sheet closing date');
    const inventoryStartSource = balanceSheetMetrics.inventoryAssetStartLabel || t('Balance Sheet opening date');
    const inventoryEndSource = balanceSheetMetrics.inventoryAssetEndLabel || t('Balance Sheet closing date');
    const cashConversionCycleRows = [
        {
            metric: t('DSO'),
            value: commercialMetrics.dso == null ? '-' : formatCount(commercialMetrics.dso),
            formula: t('Open A/R / daily net sales'),
            source: t('QuickBooks invoices and receivables aging'),
            status: commercialMetrics.dso == null ? 'unavailable' : 'success'
        },
        {
            metric: t('DIO'),
            value: commercialMetrics.dio == null ? '-' : formatCount(commercialMetrics.dio),
            formula: t('Average inventory / cost of sales x period days'),
            source: t('Balance Sheet inventory and ProfitAndLoss COGS'),
            status: commercialMetrics.dio == null ? 'unavailable' : 'success'
        },
        {
            metric: t('DPO'),
            value: commercialMetrics.dpo == null ? '-' : formatCount(commercialMetrics.dpo),
            formula: t('Average A/P / vendor bills x period days'),
            source: t('Balance Sheet A/P and QuickBooks Bills'),
            status: commercialMetrics.dpo == null ? 'unavailable' : 'success'
        },
        {
            metric: t('CCC'),
            value: commercialMetrics.cashConversionCycle == null ? '-' : formatCount(commercialMetrics.cashConversionCycle),
            formula: t('DIO + DSO - DPO'),
            source: t('Calculated from DIO, DSO and DPO'),
            status: cashConversionCycleStatus || 'unavailable'
        }
    ];
    const cashConversionCycleInputs = [
        {
            metric: t('Accounts Payable start'),
            value: formatMoney(commercialMetrics.accountsPayableStart),
            source: accountsPayableStartSource
        },
        {
            metric: t('Accounts Payable end'),
            value: formatMoney(commercialMetrics.accountsPayableEnd),
            source: accountsPayableEndSource
        },
        {
            metric: t('Average Accounts Payable'),
            value: formatMoney(commercialMetrics.averageAccountsPayable),
            source: t('Balance Sheet beginning and ending balance')
        },
        {
            metric: t('Vendor Bills'),
            value: formatMoney(commercialMetrics.totalVendorBills),
            source: t('Bills registered in the selected period')
        },
        {
            metric: t('Inventory start'),
            value: formatMoney(commercialMetrics.inventoryAssetStart),
            source: inventoryStartSource
        },
        {
            metric: t('Inventory end'),
            value: formatMoney(commercialMetrics.inventoryAssetEnd),
            source: inventoryEndSource
        },
        {
            metric: t('Average Inventory'),
            value: formatMoney(commercialMetrics.averageInventory),
            source: t('Inventory Asset beginning and ending balance')
        },
        {
            metric: t('Cost of sales'),
            value: formatMoney(commercialMetrics?.profitAndLoss?.costOfSales),
            source: t('ProfitAndLoss cost of sales')
        }
    ];
    const getCashConversionSeverity = (status) => {
        if (status === 'success') return 'success';
        if (status === 'danger') return 'danger';
        return 'warning';
    };
    const getCashConversionStatusLabel = (status) => {
        if (status === 'success') return t('Disponible');
        if (status === 'danger') return t('Fuera de meta');
        return t('No disponible');
    };
    const cashFlowProjection = commercialDashboard?.cashFlowProjection || {};
    const cashFlowRows = Array.isArray(cashFlowProjection?.rows) ? cashFlowProjection.rows : [];
    const cashFlowBankAccounts = Array.isArray(cashFlowProjection?.operatingBankAccounts) ? cashFlowProjection.operatingBankAccounts : [];
    const cashFlowCollectionBuckets = Array.isArray(cashFlowProjection?.collectionBuckets) ? cashFlowProjection.collectionBuckets : [];
    const cashFlowReceivablesReconciliation = cashFlowProjection?.receivablesReconciliation || null;
    const cashFlowRowExpansionTemplate = (rowData) => {
        const details = Array.isArray(rowData?.collectionDetails) ? rowData.collectionDetails : [];
        return (
            <div className="p-3 surface-50 border-round">
                <div className="font-semibold text-900 mb-3">
                    {t('Expected collections detail')} · {rowData?.period || '-'}
                </div>
                <DataTable value={details} responsiveLayout="scroll" emptyMessage={t('No projected collections for this week')} size="small">
                    <Column field="customer" header={t('Customer')} />
                    <Column field="invoice" header={t('Invoice #')} />
                    <Column field="dueDate" header={t('Due date')} body={(detail) => detail?.dueDate || '-'} />
                    <Column field="openAmount" header={t('Open amount')} body={(detail) => formatMoney(detail?.openAmount)} />
                    <Column field="paymentProbability" header={t('Probability')} body={(detail) => formatPercent(detail?.paymentProbability)} />
                    <Column field="expectedAmount" header={t('Expected collection')} body={(detail) => formatMoney(detail?.expectedAmount)} />
                    <Column
                        field="probabilitySource"
                        header={t('Source')}
                        body={(detail) => t(detail?.probabilitySource === 'manual' ? 'Manual review' : 'Aging fallback')}
                    />
                </DataTable>
            </div>
        );
    };
    const getLiquiditySeverity = (severity) => {
        if (severity === 'success') return 'success';
        if (severity === 'warning') return 'warning';
        return 'danger';
    };
    const getLiquidityStatusLabel = (status) => {
        if (status === 'Verde') return t('Verde');
        if (status === 'Amarillo') return t('Amarillo');
        if (status === 'Rojo') return t('Rojo');
        return t(status || '-');
    };
    const vendingReconciliationRows = Array.isArray(vendingReconciliation?.rows) ? vendingReconciliation.rows : [];
    const vendingReconciliationTotals = vendingReconciliation?.totals || {};
    const budgetVsActuals = commercialDashboard?.budgetVsActuals || {};
    const budgetVsActualRows = Array.isArray(budgetVsActuals?.rows) ? budgetVsActuals.rows : [];
    const budgetVsActualWeeks = Array.isArray(budgetVsActuals?.weeks) ? budgetVsActuals.weeks : [];
    const budgetVsActualTotals = budgetVsActuals?.totals || {};
    const budgetSourceText = [
        budgetVsActuals?.companyName,
        budgetVsActuals?.budget?.name,
        budgetVsActuals?.startDate && budgetVsActuals?.endDate ? `${budgetVsActuals.startDate} - ${budgetVsActuals.endDate}` : null
    ]
        .filter(Boolean)
        .join(' · ');
    const budgetVarianceText =
        budgetVsActualTotals?.variancePct == null
            ? '-'
            : `${formatPercent(budgetVsActualTotals.variancePct)} / ${formatMoney(budgetVsActualTotals.variance)}`;
    const vendorBillPaymentRows = useMemo(
        () => (Array.isArray(vendorBillPayments?.rows) ? vendorBillPayments.rows : []),
        [vendorBillPayments?.rows]
    );
    const vendorBillPaymentTotals = vendorBillPayments?.totals || {};
    const vendorBillPaymentAccountOptions = useMemo(
        () => [
            { label: t('Todas las cuentas'), value: '' },
            ...(Array.isArray(vendorBillPayments?.accountOptions) ? vendorBillPayments.accountOptions : [])
        ],
        [t, vendorBillPayments?.accountOptions]
    );
    const filteredVendorBillPaymentRows = useMemo(() => {
        const search = String(vendorBillPaymentSearch || '').trim().toLowerCase();
        return vendorBillPaymentRows.filter((row) => {
            const matchesAccount = !vendorBillPaymentAccountFilter || row?.account === vendorBillPaymentAccountFilter;
            if (!matchesAccount) return false;
            if (!search) return true;
            return [row?.date, row?.number, row?.vendor, row?.account, row?.paymentMethod, row?.amount]
                .some((value) => String(value ?? '').toLowerCase().includes(search));
        });
    }, [vendorBillPaymentAccountFilter, vendorBillPaymentRows, vendorBillPaymentSearch]);
    const filteredVendorBillPaymentTotal = filteredVendorBillPaymentRows.reduce(
        (sum, row) => sum + Number(row?.amount || 0),
        0
    );
    const vendorBillPaymentSourceText = [
        vendorBillPayments?.companyName,
        vendorBillPayments?.reportName,
        vendorBillPayments?.period?.startDate && vendorBillPayments?.period?.endDate
            ? `${vendorBillPayments.period.startDate} - ${vendorBillPayments.period.endDate}`
            : null
    ]
        .filter(Boolean)
        .join(' · ');
    const getBudgetVarianceSeverity = (severity) => {
        if (severity === 'success' || severity === 'warning' || severity === 'danger') return severity;
        return 'info';
    };
    const getBudgetVarianceLabel = (severity) => {
        if (severity === 'success') return t('Dentro de meta');
        if (severity === 'warning') return t('Revisar');
        if (severity === 'danger') return t('Fuera de meta');
        return t('Sin presupuesto');
    };
    const getVendingReconciliationSeverity = (status) => {
        if (status === 'Match') return 'success';
        if (status === 'Solo Parlevel' || status === 'Solo QuickBooks') return 'warning';
        if (status === 'Diferencia') return 'danger';
        return 'info';
    };
    const commercialSellersFooter = commercialDashboard?.sellers?.totals || {};
    const sellerSalesSummaryText = `${t('Sellers at 90% quota')}: ${formatCount(sellersAtNinetyPercentQuota.length)} ${t('of')} ${formatCount(sellersWithQuota.length)} | ${formatMoney(commercialSellersFooter.netSales)} ${t('net sales')} | ${formatMoney(commercialSellersFooter.quota)} ${t('quota')}`;
    const trendRows = [
        {
            label: t('Gross sales'),
            current: commercialDashboard?.trend?.current?.grossSales,
            previous: commercialDashboard?.trend?.previousMonth?.grossSales,
            delta: commercialDashboard?.trend?.delta?.grossSales
        },
        {
            label: t('Net sales'),
            current: commercialDashboard?.trend?.current?.netSales,
            previous: commercialDashboard?.trend?.previousMonth?.netSales,
            delta: commercialDashboard?.trend?.delta?.netSales
        },
        {
            label: t('Collected amount'),
            current: commercialDashboard?.trend?.current?.collected,
            previous: commercialDashboard?.trend?.previousMonth?.collected,
            delta: commercialDashboard?.trend?.delta?.collected
        }
    ];
    const trendSummaryText = `${t('Net sales')} ${formatPercent(commercialDashboard?.trend?.delta?.netSales)} | ${t('Collected amount')} ${formatPercent(commercialDashboard?.trend?.delta?.collected)}`;
    const quarterQuotaMonths = Array.isArray(quarterQuotaMeta?.periodMonths) ? quarterQuotaMeta.periodMonths : [];
    const customerQuotaMonths = Array.isArray(customerQuotaMeta?.periodMonths) ? customerQuotaMeta.periodMonths : [];
    const quotaEditorEnabled = activeTeam === 'comercial' && QUOTA_SUPPORTED_UNITS.has(selectedBusinessUnit);
    const sellerFilterOptions = useMemo(
        () => [
            { label: t('All team'), value: '' },
            ...commercialSellerOptions.map((seller) => ({ label: seller, value: seller }))
        ],
        [commercialSellerOptions, t]
    );
    const receivablesAgingBucketOptions = useMemo(
        () => [
            { label: t('All'), value: '' },
            ...receivablesAgingSummary.map((bucket) => ({
                label: t(bucket.label),
                value: bucket.key
            }))
        ],
        [receivablesAgingSummary, t]
    );
    const payablesAgingBucketOptions = useMemo(
        () => [
            { label: t('All'), value: '' },
            ...payablesAgingSummary.map((bucket) => ({
                label: t(bucket.label),
                value: bucket.key
            }))
        ],
        [payablesAgingSummary, t]
    );
    const adminMenuItems = useMemo(() => {
        if (!canReadAllQuickBooks) return [];

        const items = [
            {
                label: showDataPanel ? t('Hide Data') : t('Data'),
                icon: 'pi pi-database',
                command: () => setShowDataPanel((current) => !current)
            }
        ];

        if (quotaEditorEnabled) {
            items.push({
                label: t('Quotas'),
                icon: 'pi pi-chart-line',
                command: () => setQuotaDialogVisible(true)
            });
        }

        if (isAdminUser && selectedBusinessUnit !== 'consolidated') {
            items.push({
                label: selectedConnection?.connected ? t('Reconnect') : t('Connect'),
                icon: 'pi pi-link',
                command: () => handleConnect(selectedBusinessUnit)
            });
        }

        return items;
    }, [handleConnect, isAdminUser, canReadAllQuickBooks, quotaEditorEnabled, selectedBusinessUnit, selectedConnection?.connected, showDataPanel, t]);

    return (
        <div className="grid">
            <div className="col-12">
                <Card
                    title="QuickBooks"
                    subTitle={
                        isReadOnlyQuickBooksUser ? 'Consulta completa de QuickBooks' : isManagementOnlyUser
                            ? 'Indicadores financieros semanales'
                            : isFinanceDashboardUser
                            ? t('Finance dashboard')
                            : t('Admin connection and sync management')
                    }
                >
                    <div className="grid align-items-end">
                        {activeTeam !== 'gerencia' ? (
                            <div className="col-12 lg:col-3">
                                <label className="block text-900 font-medium mb-2">{t('Business unit')}</label>
                                <Dropdown
                                    value={selectedBusinessUnit}
                                    options={businessUnitOptions}
                                    onChange={(e) => {
                                        setSelectedBusinessUnit(e.value);
                                        setSelectedCommercialSeller('');
                                        setCommercialSellerOptions([]);
                                    }}
                                    className="w-full"
                                />
                            </div>
                        ) : null}
                        <div className="col-12 lg:col-3">
                            <label className="block text-900 font-medium mb-2">{t('Team')}</label>
                            <SelectButton
                                value={activeTeam}
                                options={teamOptions}
                                onChange={(e) => {
                                    if (e.value) setActiveTeam(e.value);
                                }}
                                className="w-full"
                            />
                        </div>
                        {activeTeam !== 'gerencia' ? (
                            <>
                                <div className="col-12 md:col-6 lg:col-2">
                                    <label className="block text-900 font-medium mb-2">{t('Seller filter')}</label>
                                    <Dropdown
                                        value={selectedCommercialSeller}
                                        options={sellerFilterOptions}
                                        onChange={(e) => setSelectedCommercialSeller(e.value)}
                                        className="w-full"
                                        disabled={!dashboardTeamUsesQuickBooksData || commercialDisabled}
                                    />
                                </div>
                                <div className="col-12 md:col-6 lg:col-2">
                                    <label className="block text-900 font-medium mb-2">{t('From')}</label>
                                    <Calendar
                                        value={commercialFromDate}
                                        onChange={(e) => setCommercialFromDate(e.value)}
                                        dateFormat="yy-mm-dd"
                                        showIcon
                                        className="w-full"
                                        maxDate={commercialToDate || undefined}
                                        disabled={!dashboardTeamUsesQuickBooksData}
                                    />
                                </div>
                                <div className="col-12 md:col-6 lg:col-2">
                                    <label className="block text-900 font-medium mb-2">{t('To')}</label>
                                    <Calendar
                                        value={commercialToDate}
                                        onChange={(e) => setCommercialToDate(e.value)}
                                        dateFormat="yy-mm-dd"
                                        showIcon
                                        className="w-full"
                                        minDate={commercialFromDate || undefined}
                                        disabled={!dashboardTeamUsesQuickBooksData}
                                    />
                                </div>
                            </>
                        ) : null}
                        <div className="col-12 lg:col-12 flex lg:justify-content-end gap-2">
                            {activeTeam !== 'gerencia' ? (
                                <>
                                    <div className="mr-auto flex align-items-center gap-2">
                                        <span className="text-sm text-600">{t('Status')}</span>
                                        <ConnectionBadge connection={selectedConnection} businessUnit={selectedBusinessUnit} t={t} />
                                    </div>
                                    <Button label={t('Refresh')} icon="pi pi-refresh" className="p-button-outlined" onClick={handleRefreshAll} loading={loading || commercialLoading} />
                                </>
                            ) : (
                                <span className="mr-auto text-sm text-600">Panel gerencial de indicadores semanales</span>
                            )}
                            {canReadAllQuickBooks ? (
                                <>
                                    <Menu id="quickbooks-admin-menu" model={adminMenuItems} popup ref={adminMenuRef} />
                                    <Button
                                        label={isReadOnlyQuickBooksUser ? 'Más vistas' : t('Manage')}
                                        icon="pi pi-cog"
                                        className="p-button-outlined"
                                        onClick={(event) => adminMenuRef.current?.toggle(event)}
                                        aria-haspopup
                                        aria-controls="quickbooks-admin-menu"
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                    {dashboardTeamUsesQuickBooksData && commercialDisabled ? (
                        <div className="mt-3">
                            <Message severity="info" text={t('This dashboard requires a specific business unit. Consolidated is not available for this endpoint.')} />
                        </div>
                    ) : null}
                    {dashboardTeamUsesQuickBooksData && dateFilterInvalid ? (
                        <div className="mt-3">
                            <Message severity="warn" text={t('The end date must be on or after the start date.')} />
                        </div>
                    ) : null}
                </Card>
            </div>

            {activeTeam === 'comercial' ? (
                <>
                    <div className="col-12">
                        <div className="grid">
                            <KpiCard
                                title={t('Invoices ($)')}
                                value={formatMoney(commercialMetrics.grossSales)}
                                hint={commercialDashboard?.period?.label || t('Selected period')}
                            />
                            <KpiCard
                                title={t('Credit Notes ($)')}
                                value={formatMoney(Math.abs(Number(commercialMetrics.creditMemos || 0)))}
                                hint={commercialDashboard?.period?.label || t('Selected period')}
                            />
                            <KpiCard
                                title={t('Net Sales')}
                                value={formatMoney(commercialMetrics.netSales)}
                                hint={commercialDashboard?.period?.label || t('Selected period')}
                            />
                            <KpiCard
                                title={t('Collected amount')}
                                value={formatMoney(commercialMetrics.collected)}
                                hint={commercialDashboard?.period?.label || t('Selected period')}
                            />
                            <KpiCard
                                title={t('% Collected')}
                                value={formatPercent(commercialMetrics.collectionRate)}
                                hint={t('Collected vs net sales')}
                            />
                            <KpiCard
                                title={t('Quota')}
                                value={formatMoney(commercialMetrics.quota)}
                                hint={commercialDashboard?.period?.label || t('Selected period')}
                            />
                            <KpiCard
                                title={t('Quota Achievement (%)')}
                                value={formatPercent(commercialMetrics.salesAttainment)}
                                hint={t('Sales vs monthly quote')}
                            />
                            <KpiCard
                                title={t('Projected Close')}
                                value={formatMoney(commercialMetrics.projectedClose)}
                                hint={`${t('Current daily run rate')} x ${formatCount(commercialMetrics.projectionDays)} ${t('days')}`}
                            />
                            <KpiCard
                                title={t('Projected Attainment')}
                                value={formatPercent(commercialMetrics.projectedAttainment)}
                                hint={t('Projected close vs quota')}
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <Card
                            title={t('Sales by Seller')}
                            className={`quickbooks-collapsible-card${sellerSalesCollapsed ? ' quickbooks-collapsible-card--collapsed' : ''}`}
                            header={
                                <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                                    <div className="text-sm text-600">{sellerSalesSummaryText}</div>
                                    <Button
                                        icon={sellerSalesCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                        className="p-button-outlined p-button-sm"
                                        onClick={() => setSellerSalesCollapsed((current) => !current)}
                                        tooltip={sellerSalesCollapsed ? t('Expand section') : t('Collapse section')}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>
                            }
                        >
                            {!sellerSalesCollapsed ? (
                                <>
                                    <div className="mb-3 p-3 border-1 border-200 border-round">
                                        <div className="text-sm text-600 mb-2">{t('Sellers at 90% quota')}</div>
                                        <div className="text-2xl font-semibold text-900">
                                            {sellersAtNinetyPercentQuota.length} {t('of')} {sellersWithQuota.length}
                                        </div>
                                        <div className="text-sm text-500 mt-2">{t('sellers at >=90% of their quota')}</div>
                                    </div>
                                    <DataTable value={commercialSellerRows} responsiveLayout="scroll" loading={commercialLoading} emptyMessage={t('No commercial data available')}>
                                        <Column field="seller" header={t('Seller')} />
                                        <Column field="invoiceCount" header={t('Invoices')} body={(rowData) => formatCount(rowData?.invoiceCount)} />
                                        <Column field="customerCount" header={t('Customers')} body={(rowData) => formatCount(rowData?.customerCount)} />
                                        <Column field="grossSales" header={t('Gross sales')} body={(rowData) => formatMoney(rowData?.grossSales)} />
                                        <Column field="creditMemos" header={t('Credit memos')} body={(rowData) => formatMoney(rowData?.creditMemos)} />
                                        <Column field="netSales" header={t('Net sales')} body={(rowData) => formatMoney(rowData?.netSales)} />
                                        <Column field="collected" header={t('Collected amount')} body={(rowData) => formatMoney(rowData?.collected)} />
                                        <Column field="quota" header={t('Quota')} body={(rowData) => formatMoney(rowData?.quota)} />
                                        <Column header={t('Sales vs quota')} body={sellerQuotaProgressTemplate} style={{ minWidth: '16rem' }} />
                                    </DataTable>
                                    {commercialSellerRows.length ? (
                                        <div className="mt-3 text-sm text-600">
                                            {t('Totals')}: {t('net sales')} {formatMoney(commercialSellersFooter.netSales)} | {t('quota')} {formatMoney(commercialSellersFooter.quota)} | {t('attainment')}{' '}
                                            {formatPercent(commercialSellersFooter.attainment)}
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </Card>
                    </div>

                    <div className="col-12">
                        <Card
                            title={t('Trend vs Previous Month')}
                            className={`quickbooks-collapsible-card${trendCollapsed ? ' quickbooks-collapsible-card--collapsed' : ''}`}
                            header={
                                <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                                    <div className="text-sm text-600">{trendSummaryText}</div>
                                    <Button
                                        icon={trendCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                        className="p-button-outlined p-button-sm"
                                        onClick={() => setTrendCollapsed((current) => !current)}
                                        tooltip={trendCollapsed ? t('Expand section') : t('Collapse section')}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>
                            }
                        >
                            {!trendCollapsed ? (
                                <DataTable value={trendRows} responsiveLayout="scroll" loading={commercialLoading} emptyMessage={t('No trend data available')}>
                                    <Column field="label" header={t('Metric')} />
                                    <Column field="current" header={t('Current')} body={(rowData) => formatMoney(rowData?.current)} />
                                    <Column field="previous" header={t('Previous month')} body={(rowData) => formatMoney(rowData?.previous)} />
                                    <Column field="delta" header={t('Delta')} body={(rowData) => formatPercent(rowData?.delta)} />
                                </DataTable>
                            ) : null}
                        </Card>
                    </div>

                    <div className="col-12">
                        <Card
                            title={t('Customers below purchase pace')}
                            className={`quickbooks-collapsible-card${repurchaseRiskCollapsed ? ' quickbooks-collapsible-card--collapsed' : ''}`}
                            header={
                                <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                                    <div className="text-sm text-600">{repurchaseRiskSummaryText}</div>
                                    <Button
                                        icon={repurchaseRiskCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                        className="p-button-outlined p-button-sm"
                                        onClick={() => setRepurchaseRiskCollapsed((current) => !current)}
                                        tooltip={repurchaseRiskCollapsed ? t('Expand section') : t('Collapse section')}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>
                            }
                        >
                            {!repurchaseRiskCollapsed ? (
                                <>
                                    <div className="grid mb-3">
                                        <div className="col-12 md:col-3">
                                            <div className="p-3 border-1 border-200 border-round h-full">
                                                <div className="text-sm text-600 mb-2">{t('Customers at risk')}</div>
                                                <div className="text-2xl font-semibold text-900">{formatCount(repurchaseRiskTotals.atRiskCustomerCount)}</div>
                                                <div className="text-xs text-500 mt-2">{t('drop vs trailing average')}</div>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-3">
                                            <div className="p-3 border-1 border-200 border-round h-full">
                                                <div className="text-sm text-600 mb-2">{t('Estimated gap')}</div>
                                                <div className="text-2xl font-semibold text-900">{formatMoney(repurchaseRiskTotals.estimatedGap)}</div>
                                                <div className="text-xs text-500 mt-2">{t('current purchase vs average')}</div>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-3">
                                            <div className="p-3 border-1 border-200 border-round h-full">
                                                <div className="text-sm text-600 mb-2">{t('Critical customers')}</div>
                                                <div className="text-2xl font-semibold text-900">{formatCount(repurchaseRiskTotals.criticalCustomerCount)}</div>
                                                <div className="text-xs text-500 mt-2">{t('highest urgency')}</div>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-3">
                                            <div className="p-3 border-1 border-200 border-round h-full">
                                                <div className="text-sm text-600 mb-2">{t('Average drop')}</div>
                                                <div className="text-2xl font-semibold text-900">{formatPercent(repurchaseRiskTotals.averageDropPercent)}</div>
                                                <div className="text-xs text-500 mt-2">{t('at-risk customers')}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid align-items-end mb-3">
                                        <div className="col-12 md:col-3">
                                            <label className="block text-900 font-medium mb-2">{t('Importance')}</label>
                                            <Dropdown
                                                value={repurchaseImportanceFilter}
                                                options={repurchaseImportanceOptions}
                                                onChange={(e) => setRepurchaseImportanceFilter(e.value)}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                    <DataTable
                                        value={filteredRepurchaseRiskRows}
                                        responsiveLayout="scroll"
                                        loading={commercialLoading}
                                        emptyMessage={t('No repurchase alerts available')}
                                        paginator={filteredRepurchaseRiskRows.length > 10}
                                        rows={10}
                                    >
                                        <Column field="customer" header={t('Customer')} />
                                        <Column field="seller" header={t('Seller')} />
                                        <Column field="currentSales" header={t('Current purchase')} body={(rowData) => formatMoney(rowData?.currentSales)} />
                                        <Column field="trailingAverage" header={t('Trailing 3-month average')} body={(rowData) => formatMoney(rowData?.trailingAverage)} />
                                        <Column field="dropPercent" header={t('Drop')} body={(rowData) => formatPercent(rowData?.dropPercent)} />
                                        <Column field="gapAmount" header={t('Gap')} body={(rowData) => formatMoney(rowData?.gapAmount)} />
                                        <Column field="lastPurchaseAt" header={t('Last purchase')} body={(rowData) => formatDateTime(rowData?.lastPurchaseAt)} />
                                        <Column
                                            field="importance"
                                            header={t('Importance')}
                                            body={(rowData) => <Tag value={t(rowData?.importance || 'Low')} severity={getRepurchaseImportanceSeverity(rowData?.importance)} />}
                                        />
                                    </DataTable>
                                </>
                            ) : null}
                        </Card>
                    </div>

                    <div className="col-12 lg:col-7">
                        <Card
                            title={t('Quota Pace')}
                            className={`quickbooks-collapsible-card${quotaPaceCollapsed ? ' quickbooks-collapsible-card--collapsed' : ''} mt-3`}
                            header={
                                <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                                    <div className="text-sm text-600">
                                        {formatCount(quotaPaceOnTrackCount)} {t('of')} {formatCount(quotaPaceRows.length)} {t('sellers on pace')}
                                    </div>
                                    <Button
                                        icon={quotaPaceCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                        className="p-button-outlined p-button-sm"
                                        onClick={() => setQuotaPaceCollapsed((current) => !current)}
                                        tooltip={quotaPaceCollapsed ? t('Expand section') : t('Collapse section')}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>
                            }
                        >
                            {!quotaPaceCollapsed ? (
                                <>
                                    <div className="flex justify-content-end mb-3">
                                        <SelectButton
                                            value={quotaPacePeriod}
                                            options={quotaPacePeriodOptions}
                                            onChange={(e) => e.value && setQuotaPacePeriod(e.value)}
                                            className="w-full md:w-auto"
                                        />
                                    </div>
                                    {quotaPaceProjectionText ? (
                                        <div className="mb-3 p-3 border-1 border-200 border-round bg-blue-50 text-blue-900 font-medium">
                                            {quotaPaceProjectionText}
                                        </div>
                                    ) : null}
                                    <DataTable value={quotaPaceRows} responsiveLayout="scroll" loading={commercialLoading} emptyMessage={t('No quota pace data available')}>
                                        <Column field="seller" header={t('Seller')} />
                                        <Column field="currentDailyRunRate" header={t('Current daily run rate')} body={(rowData) => formatMoney(rowData?.currentDailyRunRate)} />
                                        <Column field="projectedClose" header={projectedCloseHeader} body={(rowData) => formatMoney(rowData?.projectedClose)} />
                                        <Column field="projectedAttainment" header={t('Projected attainment')} body={(rowData) => formatPercent(rowData?.projectedAttainment)} />
                                        <Column field="requiredDailyToClose" header={t('Required daily to close')} body={(rowData) => formatMoney(rowData?.requiredDailyToClose)} />
                                        <Column
                                            field="status"
                                            header={t('Status')}
                                            body={(rowData) => <Tag value={t(rowData?.status || 'No quota')} severity={rowData?.severity || 'info'} />}
                                        />
                                    </DataTable>
                                </>
                            ) : null}
                        </Card>

                        <ReceivablesAgingCard
                            t={t}
                            title={t('Accounts Receivable Aging')}
                            collapsed={receivablesAgingCollapsed}
                            onToggle={() => setReceivablesAgingCollapsed((current) => !current)}
                            total={receivablesAgingTotal}
                            rows={receivablesAgingRows}
                            filteredRows={filteredReceivablesAgingRows}
                            filteredTotal={filteredReceivablesAgingTotal}
                            summary={receivablesAgingSummary}
                            bucketFilter={receivablesAgingBucketFilter}
                            bucketOptions={receivablesAgingBucketOptions}
                            onBucketChange={setReceivablesAgingBucketFilter}
                            onReviewEdit={isReadOnlyQuickBooksUser ? null : handleOpenReceivableReviewDialog}
                            savingReviewKey={savingReceivableReviewKey}
                            loading={commercialLoading}
                        />
                    </div>

                    <div className="col-12 lg:col-5">
                        <Card
                            title={t('Top Customers')}
                            className={`quickbooks-collapsible-card${topCustomersCollapsed ? ' quickbooks-collapsible-card--collapsed' : ''}`}
                            header={
                                <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                                    <div className="text-sm text-600">
                                        {formatCount(commercialCustomers.length)} {t('customers')}
                                    </div>
                                    <div className="flex justify-content-end gap-2">
                                        {!topCustomersCollapsed && commercialCustomers.length > 10 ? (
                                            <Button
                                                label={showAllCommercialCustomers ? t('Show top 10') : t('Show all')}
                                                icon={showAllCommercialCustomers ? 'pi pi-compress' : 'pi pi-list'}
                                                className="p-button-outlined p-button-sm"
                                                onClick={() => setShowAllCommercialCustomers((current) => !current)}
                                            />
                                        ) : null}
                                        <Button
                                            icon="pi pi-pencil"
                                            className="p-button-outlined p-button-sm"
                                            onClick={openCustomerQuotaDialog}
                                            tooltip={t('Edit customer quotas')}
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                        <Button
                                            icon={topCustomersCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                            className="p-button-outlined p-button-sm"
                                            onClick={() => setTopCustomersCollapsed((current) => !current)}
                                            tooltip={topCustomersCollapsed ? t('Expand section') : t('Collapse section')}
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    </div>
                                </div>
                            }
                        >
                            {!topCustomersCollapsed ? (
                                <DataTable
                                    value={visibleCommercialCustomers}
                                    responsiveLayout="scroll"
                                    loading={commercialLoading}
                                    emptyMessage={t('No customer data available')}
                                    paginator={showAllCommercialCustomers}
                                    rows={10}
                                >
                                    <Column field="name" header={t('Customer')} />
                                    <Column field="total" header={t('Net sales')} body={(rowData) => formatMoney(rowData?.total)} />
                                    <Column field="quota" header={t('Quota')} body={(rowData) => formatMoney(rowData?.quota)} />
                                    <Column field="attainment" header={t('Attainment')} body={(rowData) => formatPercent(rowData?.attainment)} />
                                    <Column
                                        header={t('Share')}
                                        body={(rowData) =>
                                            commercialMetrics.netSales
                                                ? formatPercent((Number(rowData?.total || 0) / Number(commercialMetrics.netSales || 0)) * 100)
                                                : '-'
                                        }
                                    />
                                </DataTable>
                            ) : null}
                        </Card>

                        <Card
                            title={t('Product Mix')}
                            className={`quickbooks-collapsible-card${productMixCollapsed ? ' quickbooks-collapsible-card--collapsed' : ''} mt-3`}
                            header={
                                <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                                    <div className="text-sm text-600">
                                        {formatCount(productMixRows.length)} {t('categories')}
                                    </div>
                                    <Button
                                        icon={productMixCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                        className="p-button-outlined p-button-sm"
                                        onClick={() => setProductMixCollapsed((current) => !current)}
                                        tooltip={productMixCollapsed ? t('Expand section') : t('Collapse section')}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>
                            }
                        >
                            {!productMixCollapsed ? (
                                <DataTable
                                    value={productMixRows}
                                    responsiveLayout="scroll"
                                    loading={commercialLoading}
                                    emptyMessage={t('No product data available')}
                                >
                                    <Column field="product" header={t('Product')} />
                                    <Column field="amount" header={t('Sales')} body={(rowData) => formatMoney(rowData?.amount)} />
                                    <Column field="share" header={t('% of total sales')} body={(rowData) => formatPercent(rowData?.share)} />
                                </DataTable>
                            ) : null}
                        </Card>
                    </div>

                    <div className="col-12">
                        <Card
                            title={t('Cross-selling by Customer')}
                            className={`quickbooks-collapsible-card${crossSellingCollapsed ? ' quickbooks-collapsible-card--collapsed' : ''}`}
                            header={
                                <div className="flex justify-content-between align-items-center gap-3 p-3 pb-0">
                                    <div className="text-sm text-600">{crossSellingSummaryText}</div>
                                    <Button
                                        icon={crossSellingCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                        className="p-button-outlined p-button-sm"
                                        onClick={() => setCrossSellingCollapsed((current) => !current)}
                                        tooltip={crossSellingCollapsed ? t('Expand section') : t('Collapse section')}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>
                            }
                        >
                            {!crossSellingCollapsed ? (
                                <>
                                    <div className="flex justify-content-end mb-3">
                                        <SelectButton
                                            value={crossSellingView}
                                            options={crossSellingViewOptions}
                                            onChange={(e) => e.value && setCrossSellingView(e.value)}
                                            className="w-full md:w-auto"
                                        />
                                    </div>
                                    <div className="grid mb-3">
                                <div className="col-12 md:col-3">
                                    <div className="p-3 border-1 border-200 border-round h-full">
                                        <div className="text-sm text-600 mb-2">
                                            {crossSellingView === 'opportunities' ? t('Opportunities') : t('Customers with new categories')}
                                        </div>
                                        <div className="text-2xl font-semibold text-900">
                                            {formatCount(crossSellingView === 'opportunities' ? opportunityTotals.opportunityCount : crossSellingTotals.customersWithNewCategories)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 md:col-3">
                                    <div className="p-3 border-1 border-200 border-round h-full">
                                        <div className="text-sm text-600 mb-2">
                                            {crossSellingView === 'opportunities' ? t('Potential sales') : t('New category sales')}
                                        </div>
                                        <div className="text-2xl font-semibold text-900">
                                            {formatMoney(crossSellingView === 'opportunities' ? opportunityTotals.potentialSales : crossSellingTotals.newCategorySales)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 md:col-3">
                                    <div className="p-3 border-1 border-200 border-round h-full">
                                        <div className="text-sm text-600 mb-2">
                                            {crossSellingView === 'opportunities' ? t('High priority') : t('New families')}
                                        </div>
                                        <div className="text-2xl font-semibold text-900">
                                            {formatCount(crossSellingView === 'opportunities' ? opportunityTotals.highPriorityCount : crossSellingTotals.newFamilyCount)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 md:col-3">
                                    <div className="p-3 border-1 border-200 border-round h-full">
                                        <div className="text-sm text-600 mb-2">
                                            {crossSellingView === 'opportunities' ? t('Customers with opportunities') : t('New category types')}
                                        </div>
                                        <div className="text-2xl font-semibold text-900">
                                            {formatCount(crossSellingView === 'opportunities' ? opportunityTotals.customerCount : crossSellingTotals.newCategoryCount)}
                                        </div>
                                    </div>
                                </div>
                                    </div>
                                    <div className="grid align-items-end mb-3">
                                {crossSellingView === 'detected' ? (
                                    <div className="col-12 md:col-4">
                                        <label className="block text-900 font-medium mb-2">{t('Status')}</label>
                                        <Dropdown
                                            value={crossSellingStatusFilter}
                                            options={crossSellingStatusOptions}
                                            onChange={(e) => setCrossSellingStatusFilter(e.value)}
                                            className="w-full"
                                        />
                                    </div>
                                ) : (
                                    <div className="col-12 md:col-4">
                                        <label className="block text-900 font-medium mb-2">{t('Priority')}</label>
                                        <Dropdown
                                            value={opportunityPriorityFilter}
                                            options={opportunityPriorityOptions}
                                            onChange={(e) => setOpportunityPriorityFilter(e.value)}
                                            className="w-full"
                                        />
                                    </div>
                                )}
                                <div className="col-12 md:col-4">
                                    <label className="block text-900 font-medium mb-2">{t('Category')}</label>
                                    <Dropdown
                                        value={crossSellingCategoryFilter}
                                        options={crossSellingCategoryOptions}
                                        onChange={(e) => setCrossSellingCategoryFilter(e.value)}
                                        className="w-full"
                                        filter
                                    />
                                </div>
                                    </div>
                                    {crossSellingView === 'detected' ? (
                                        <DataTable
                                            value={filteredCrossSellingRows}
                                            responsiveLayout="scroll"
                                            loading={commercialLoading}
                                            emptyMessage={t('No cross-selling data available')}
                                            paginator={filteredCrossSellingRows.length > 10}
                                            rows={10}
                                        >
                                            <Column field="customer" header={t('Customer')} />
                                            <Column field="seller" header={t('Seller')} />
                                            <Column header={t('Historical categories')} body={(rowData) => formatListValue(rowData?.historicalLevel2)} />
                                            <Column header={t('Current categories')} body={(rowData) => formatListValue(rowData?.currentLevel2)} />
                                            <Column header={t('New categories')} body={(rowData) => formatListValue(rowData?.newLevel2)} />
                                            <Column field="newCategorySales" header={t('New category sales')} body={(rowData) => formatMoney(rowData?.newCategorySales)} />
                                            <Column field="totalSales" header={t('Total sales')} body={(rowData) => formatMoney(rowData?.totalSales)} />
                                            <Column field="newSalesShare" header={t('% new sales')} body={(rowData) => formatPercent(rowData?.newSalesShare)} />
                                            <Column
                                                field="status"
                                                header={t('Status')}
                                                body={(rowData) => <Tag value={t(rowData?.status || 'No change')} severity={getCrossSellingSeverity(rowData?.status)} />}
                                            />
                                        </DataTable>
                                    ) : (
                                        <DataTable
                                            value={filteredOpportunityRows}
                                            responsiveLayout="scroll"
                                            loading={commercialLoading}
                                            emptyMessage={t('No cross-selling opportunities available')}
                                            paginator={filteredOpportunityRows.length > 10}
                                            rows={10}
                                            dataKey="opportunityKey"
                                            expandedRows={expandedOpportunityRows}
                                            onRowToggle={(e) => setExpandedOpportunityRows(e.data)}
                                            rowExpansionTemplate={opportunityProductExpansionTemplate}
                                        >
                                            <Column expander={isOpportunityExpandable} style={{ width: '3rem' }} />
                                            <Column field="customer" header={t('Customer')} />
                                            <Column field="seller" header={t('Seller')} />
                                            <Column header={t('Current categories')} body={(rowData) => formatListValue(rowData?.currentCategories)} />
                                            <Column field="suggestedCategory" header={t('Suggested category')} />
                                            <Column field="reason" header={t('Reason')} body={(rowData) => t(rowData?.reason || '-')} />
                                            <Column header={t('Similar customers')} body={(rowData) => formatListValue(rowData?.similarCustomers)} />
                                            <Column field="potentialSales" header={t('Potential sales')} body={(rowData) => formatMoney(rowData?.potentialSales)} />
                                            <Column field="lastPurchaseAt" header={t('Last purchase')} body={(rowData) => formatDateTime(rowData?.lastPurchaseAt)} />
                                            <Column
                                                field="priority"
                                                header={t('Priority')}
                                                body={(rowData) => <Tag value={t(rowData?.priority || 'Low')} severity={getOpportunityPrioritySeverity(rowData?.priority)} />}
                                            />
                                        </DataTable>
                                    )}
                                </>
                            ) : null}
                        </Card>
                    </div>

                </>
            ) : null}

            {activeTeam === 'gerencia' ? (
                <div className="col-12">
                    {managementCycleLoaded ? (
                        <OkrFinancialIndicators selectedCycle={managementCycle} />
                    ) : (
                        <Card>
                            <div className="flex flex-column align-items-center gap-3 p-5">
                                <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                                <span className="text-600">Preparando el ciclo gerencial…</span>
                            </div>
                        </Card>
                    )}
                </div>
            ) : null}

            {activeTeam === 'finanzas' ? (
                <div className="col-12">
                    <div className="grid">
                        <KpiCard
                            title={t('Accounts Receivables')}
                            value={formatMoney(financeReceivablesTotal)}
                            hint={t('Open receivables through the date cutoff')}
                            className="col-12 md:col-6 xl:col-4"
                        />
                        <KpiCard
                            title={t('Overdue Accounts Receivable')}
                            value={formatMoney(financeOverdueReceivables)}
                            hint={financeOverdueReceivablesHint}
                            className="col-12 md:col-6 xl:col-4"
                        />
                        <KpiCard
                            title={t('Total cobrado')}
                            value={formatMoney(commercialMetrics.collected)}
                            hint={financePeriodLabel}
                            className="col-12 md:col-6 xl:col-4"
                        />
                        <KpiCard
                            title={t('DSO')}
                            value={commercialMetrics.dso == null ? '-' : formatCount(commercialMetrics.dso)}
                            hint={t('Days sales outstanding by business unit')}
                            className="col-12 md:col-6 xl:col-4"
                        />
                        <KpiCard
                            title={t('% cobrado')}
                            value={formatPercent(commercialMetrics.collectionRate)}
                            hint={t('Collected vs net sales for selected period')}
                            className="col-12 md:col-6 xl:col-4"
                        />
                        <KpiCard
                            title={t('Margen Bruto')}
                            value={formatPercent(commercialMetrics.grossMargin)}
                            hint={t('ProfitAndLoss gross profit over net sales')}
                            className="col-12 md:col-6 xl:col-4"
                        />
                        <KpiCard
                            title={t('Margen Neto')}
                            value={formatPercent(commercialMetrics.netMargin)}
                            hint={t('ProfitAndLoss net income over net sales')}
                            className="col-12 md:col-6 xl:col-4"
                        />
                        <KpiCard
                            title={t('Accounts Payables')}
                            value={formatMoney(financePayablesTotal)}
                            hint={t('Open vendor bills through the date cutoff')}
                            className="col-12 md:col-6 xl:col-4"
                        />
                    </div>
                    <Card
                        title={t('Cash Conversion Cycle')}
                        className="mt-3"
                        header={
                            <div className="flex justify-content-end align-items-center gap-3 p-3 pb-0">
                                <Button
                                    icon={cashConversionCycleCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => setCashConversionCycleCollapsed((current) => !current)}
                                    tooltip={cashConversionCycleCollapsed ? t('Expand section') : t('Collapse section')}
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                        }
                    >
                        {!cashConversionCycleCollapsed ? (
                            <>
                                <div className="grid mb-3">
                                    <KpiCard
                                        title={t('CCC')}
                                        value={commercialMetrics.cashConversionCycle == null ? '-' : formatCount(commercialMetrics.cashConversionCycle)}
                                        hint={cashConversionCycleHint}
                                        className="col-12 md:col-4"
                                    />
                                    <KpiCard
                                        title={t('DPO')}
                                        value={commercialMetrics.dpo == null ? '-' : formatCount(commercialMetrics.dpo)}
                                        hint={t('Days payable outstanding by business unit')}
                                        className="col-12 md:col-4"
                                    />
                                    <KpiCard
                                        title={t('DIO')}
                                        value={commercialMetrics.dio == null ? '-' : formatCount(commercialMetrics.dio)}
                                        hint={t('Days inventory outstanding')}
                                        className="col-12 md:col-4"
                                    />
                                </div>
                                <DataTable
                                    value={cashConversionCycleRows}
                                    responsiveLayout="scroll"
                                    loading={commercialLoading}
                                    emptyMessage={t('No cash conversion cycle data available')}
                                    className="mb-3"
                                >
                                    <Column field="metric" header={t('Métrica')} />
                                    <Column field="value" header={t('Días')} />
                                    <Column field="formula" header={t('Fórmula')} />
                                    <Column field="source" header={t('Fuente')} />
                                    <Column
                                        field="status"
                                        header={t('Estado')}
                                        body={(rowData) => (
                                            <Tag
                                                value={getCashConversionStatusLabel(rowData?.status)}
                                                severity={getCashConversionSeverity(rowData?.status)}
                                            />
                                        )}
                                    />
                                </DataTable>
                                <DataTable
                                    value={cashConversionCycleInputs}
                                    responsiveLayout="scroll"
                                    loading={commercialLoading}
                                    emptyMessage={t('No cash conversion cycle inputs available')}
                                >
                                    <Column field="metric" header={t('Dato base')} />
                                    <Column field="value" header={t('Valor')} />
                                    <Column field="source" header={t('Fuente')} />
                                </DataTable>
                                <Message
                                    severity="info"
                                    text={t('DPO uses only Bills registered in QuickBooks; direct bank or petty cash payments are not included.')}
                                    className="mt-3 w-full"
                                />
                            </>
                        ) : null}
                    </Card>
                    <Card title={t('Cash Flow Projection')} className="mt-3">
                        <div className="flex justify-content-end gap-2 mb-3">
                            <Button
                                type="button"
                                icon={cashFlowTableCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                onClick={() => setCashFlowTableCollapsed((current) => !current)}
                                className="p-button-sm p-button-outlined"
                                tooltip={cashFlowTableCollapsed ? t('Expand section') : t('Collapse section')}
                                tooltipOptions={{ position: 'top' }}
                            />
                            <Button
                                type="button"
                                icon="pi pi-cog"
                                label={t('Settings')}
                                onClick={() => setCashFlowSettingsVisible(true)}
                                className="p-button-sm p-button-outlined"
                            />
                        </div>
                        <div className="grid mb-3">
                            <KpiCard
                                title={t('Starting cash')}
                                value={formatMoney(cashFlowProjection.startingCash)}
                                hint={t('Operating bank accounts only')}
                                className="col-12 md:col-4"
                            />
                            <KpiCard
                                title={t('Minimum projected cash')}
                                value={formatMoney(cashFlowProjection.minimumProjectedCash)}
                                hint={`${t('Minimum liquidity')}: ${formatMoney(cashFlowProjection.minimumLiquidity || 1000)}`}
                                className="col-12 md:col-4"
                            />
                            <KpiCard
                                title={t('Critical week')}
                                value={cashFlowProjection.criticalWeek?.week ? `${t('Week')} ${cashFlowProjection.criticalWeek.week}` : '-'}
                                hint={cashFlowProjection.criticalWeek?.period || t('No projection data available')}
                                className="col-12 md:col-4"
                            />
                        </div>
                        {cashFlowReceivablesReconciliation?.status === 'difference' ? (
                            <Message
                                severity="warn"
                                text={`${t('Accounts receivable detail does not reconcile with the official QuickBooks total.')} ${t('Official total')}: ${formatMoney(cashFlowReceivablesReconciliation.reportedTotal)} · ${t('Detail used')}: ${formatMoney(cashFlowReceivablesReconciliation.detailTotal)} · ${t('Difference')}: ${formatMoney(cashFlowReceivablesReconciliation.difference)}. ${t('Review this difference before using the projection for decisions.')}`}
                                className="mb-3 w-full"
                            />
                        ) : null}
                        {!cashFlowTableCollapsed ? (
                            <DataTable
                                value={cashFlowRows}
                                responsiveLayout="scroll"
                                loading={commercialLoading}
                                emptyMessage={t('No cash flow projection data available')}
                                className="mb-3"
                                dataKey="week"
                                expandedRows={expandedCashFlowRows}
                                onRowToggle={(event) => setExpandedCashFlowRows(event.data)}
                                rowExpansionTemplate={cashFlowRowExpansionTemplate}
                            >
                                <Column expander={(rowData) => Array.isArray(rowData?.collectionDetails) && rowData.collectionDetails.length > 0} style={{ width: '3rem' }} />
                                <Column field="period" header={t('Semana')} />
                                <Column field="beginningCash" header={t('Caja inicial')} body={(rowData) => formatMoney(rowData?.beginningCash)} />
                                <Column field="scheduledReceivables" header={t('Cartera programada')} body={(rowData) => formatMoney(rowData?.scheduledReceivables)} />
                                <Column field="collectionProbability" header={t('Probabilidad ponderada')} body={(rowData) => formatPercent(rowData?.collectionProbability)} />
                                <Column field="expectedCollections" header={t('Cobros esperados')} body={(rowData) => formatMoney(rowData?.expectedCollections)} />
                                <Column field="manualCollections" header={t('Cobros manuales')} body={(rowData) => formatMoney(rowData?.manualCollections)} />
                                <Column field="vendorPayments" header={t('Bills proveedores')} body={(rowData) => formatMoney(rowData?.vendorPayments)} />
                                <Column field="manualPayments" header={t('Pagos manuales')} body={(rowData) => formatMoney(rowData?.manualPayments)} />
                                <Column field="fixedExpenses" header={t('Gastos fijos')} body={(rowData) => formatMoney(rowData?.fixedExpenses)} />
                                <Column field="financialOutflows" header={t('Préstamos/intereses')} body={(rowData) => formatMoney(rowData?.financialOutflows)} />
                                <Column field="vehicleMaintenanceReserve" header={t('Reserva autos')} body={(rowData) => formatMoney(rowData?.vehicleMaintenanceReserve)} />
                                <Column field="endingCash" header={t('Caja final')} body={(rowData) => formatMoney(rowData?.endingCash)} />
                                <Column
                                    field="liquidityStatus"
                                    header={t('Liquidez')}
                                    body={(rowData) => (
                                        <Tag
                                            value={getLiquidityStatusLabel(rowData?.liquidityStatus)}
                                            severity={getLiquiditySeverity(rowData?.severity)}
                                        />
                                    )}
                                />
                            </DataTable>
                        ) : null}
                        <div className="grid">
                            <div className="col-12 lg:col-6">
                                <DataTable
                                    value={cashFlowCollectionBuckets}
                                    responsiveLayout="scroll"
                                    loading={commercialLoading}
                                    emptyMessage={t('No collection probability data available')}
                                >
                                    <Column field="bucketLabel" header={t('Bucket CxC')} />
                                    <Column field="openAmount" header={t('Monto abierto')} body={(rowData) => formatMoney(rowData?.openAmount)} />
                                    <Column field="probability" header={t('Probabilidad')} body={(rowData) => formatPercent(rowData?.probability)} />
                                    <Column field="expectedAmount" header={t('Cobro esperado')} body={(rowData) => formatMoney(rowData?.expectedAmount)} />
                                </DataTable>
                            </div>
                            <div className="col-12 lg:col-6">
                                <DataTable
                                    value={cashFlowBankAccounts}
                                    responsiveLayout="scroll"
                                    loading={commercialLoading}
                                    emptyMessage={t('No operating bank accounts available')}
                                >
                                    <Column field="label" header={t('Cuenta banco')} />
                                    <Column field="amount" header={t('Saldo')} body={(rowData) => formatMoney(rowData?.amount)} />
                                </DataTable>
                            </div>
                        </div>
                        <Message
                            severity="info"
                            text={t('Cash flow projection uses invoice-level A/R, Monday-Saturday weeks, open Bills, trailing three-month fixed expenses, financial outflows and vehicle maintenance reserve.')}
                            className="mt-3 w-full"
                        />
                    </Card>
                    <Card
                        title={t('Accrual vs Cash')}
                        className={`quickbooks-collapsible-card${accrualCashCollapsed ? ' quickbooks-collapsible-card--collapsed' : ''} mt-3`}
                        header={
                            <div className="flex justify-content-end align-items-center gap-3 p-3 pb-0">
                                <Button
                                    icon={accrualCashCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => setAccrualCashCollapsed((current) => !current)}
                                    tooltip={accrualCashCollapsed ? t('Expand section') : t('Collapse section')}
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                        }
                    >
                        {!accrualCashCollapsed ? (
                            <>
                                <div className="grid">
                                    <KpiCard
                                        title={t('Ventas Netas')}
                                        value={formatMoney(financeNetSales)}
                                        hint={t('Accrual for selected period')}
                                        className="col-12 md:col-6"
                                    />
                                    <KpiCard
                                        title={t('Cobrado')}
                                        value={formatMoney(financeCollected)}
                                        hint={t('Cash received in selected period')}
                                        className="col-12 md:col-6"
                                    />
                                </div>
                                <div className="surface-50 border-round p-3 mt-2">
                                    <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
                                        <div>
                                            <div className="text-sm text-600">{t('Cash vs Accrual')}</div>
                                            <div className="text-lg font-semibold text-900">{financeCashDeltaText}</div>
                                            <div className="text-sm text-500 mt-1">{t('Period collections may include invoices from prior months.')}</div>
                                        </div>
                                        <div className="text-2xl font-semibold text-900">{financeCashDeltaPercent}</div>
                                    </div>
                                </div>
                                <Card title={t('Presupuesto vs Real')} className="mt-3">
                                    {budgetSourceText ? <div className="text-sm text-600 mb-3">QuickBooks · {budgetSourceText}</div> : null}
                                    {budgetVsActuals?.error ? <Message severity="warn" text={budgetVsActuals.error} className="mb-3 w-full" /> : null}
                                    <div className="grid mb-3">
                                        <KpiCard
                                            title={t('Presupuesto gastos')}
                                            value={formatMoney(budgetVsActualTotals?.budget)}
                                            hint={financePeriodLabel}
                                            className="col-12 md:col-4"
                                        />
                                        <KpiCard
                                            title={t('Real gastos')}
                                            value={formatMoney(budgetVsActualTotals?.actual)}
                                            hint="QuickBooks ProfitAndLoss"
                                            className="col-12 md:col-4"
                                        />
                                        <KpiCard
                                            title={t('Variación')}
                                            value={budgetVarianceText}
                                            hint={`${t('Umbral')}: ${formatPercent(budgetVsActualTotals?.thresholdPct)}`}
                                            className="col-12 md:col-4"
                                        />
                                    </div>
                                    <div className="grid">
                                        <div className="col-12 xl:col-6">
                                            <DataTable
                                                value={budgetVsActualWeeks}
                                                responsiveLayout="scroll"
                                                loading={commercialLoading}
                                                emptyMessage={t('No budget variance data available')}
                                                paginator={budgetVsActualWeeks.length > 6}
                                                rows={6}
                                            >
                                                <Column field="period" header={t('Semana')} />
                                                <Column field="budget" header={t('Presupuesto')} body={(rowData) => formatMoney(rowData?.budget)} />
                                                <Column field="actual" header={t('Real')} body={(rowData) => formatMoney(rowData?.actual)} />
                                                <Column field="variance" header={t('Variación')} body={(rowData) => formatMoney(rowData?.variance)} />
                                                <Column field="variancePct" header={t('% variación')} body={(rowData) => formatPercent(rowData?.variancePct)} />
                                                <Column field="thresholdPct" header={t('Umbral')} body={(rowData) => formatPercent(rowData?.thresholdPct)} />
                                                <Column
                                                    field="severity"
                                                    header={t('Estado')}
                                                    body={(rowData) => (
                                                        <Tag
                                                            value={getBudgetVarianceLabel(rowData?.severity)}
                                                            severity={getBudgetVarianceSeverity(rowData?.severity)}
                                                        />
                                                    )}
                                                />
                                            </DataTable>
                                        </div>
                                        <div className="col-12 xl:col-6">
                                            <DataTable
                                                value={budgetVsActualRows}
                                                responsiveLayout="scroll"
                                                loading={commercialLoading}
                                                emptyMessage={t('No budget variance data available')}
                                                paginator={budgetVsActualRows.length > 8}
                                                rows={8}
                                            >
                                                <Column field="label" header={t('Categoría')} />
                                                <Column field="budget" header={t('Presupuesto')} body={(rowData) => formatMoney(rowData?.budget)} />
                                                <Column field="actual" header={t('Real')} body={(rowData) => formatMoney(rowData?.actual)} />
                                                <Column field="variance" header={t('Variación')} body={(rowData) => formatMoney(rowData?.variance)} />
                                                <Column field="variancePct" header={t('% variación')} body={(rowData) => formatPercent(rowData?.variancePct)} />
                                                <Column field="thresholdPct" header={t('Umbral')} body={(rowData) => formatPercent(rowData?.thresholdPct)} />
                                                <Column
                                                    field="severity"
                                                    header={t('Estado')}
                                                    body={(rowData) => (
                                                        <Tag
                                                            value={getBudgetVarianceLabel(rowData?.severity)}
                                                            severity={getBudgetVarianceSeverity(rowData?.severity)}
                                                        />
                                                    )}
                                                />
                                            </DataTable>
                                        </div>
                                    </div>
                                </Card>
                            </>
                        ) : null}
                    </Card>
                    <Card title={t('Pagos de facturas de proveedores')} className="mt-3">
                        {vendorBillPaymentSourceText ? (
                            <div className="text-sm text-600 mb-3">QuickBooks · {vendorBillPaymentSourceText}</div>
                        ) : null}
                        {vendorBillPayments?.error ? (
                            <Message severity="warn" text={vendorBillPayments.error} className="mb-3 w-full" />
                        ) : null}
                        <div className="grid mb-3">
                            <KpiCard
                                title={t('Total pagado')}
                                value={formatMoney(vendorBillPaymentTotals?.totalPaid)}
                                hint={vendorBillPayments?.period?.label || financePeriodLabel}
                                className="col-12 md:col-4"
                            />
                            <KpiCard
                                title={t('Cantidad de pagos')}
                                value={formatCount(vendorBillPaymentTotals?.paymentCount)}
                                hint={`${formatCount(vendorBillPaymentTotals?.vendorCount)} ${t('proveedores')}`}
                                className="col-12 md:col-4"
                            />
                            <KpiCard
                                title={t('Total filtrado')}
                                value={formatMoney(filteredVendorBillPaymentTotal)}
                                hint={`${formatCount(filteredVendorBillPaymentRows.length)} ${t('pagos visibles')}`}
                                className="col-12 md:col-4"
                            />
                        </div>
                        <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2 mb-3">
                            <div className="flex flex-column md:flex-row gap-2 flex-1">
                                <span className="p-input-icon-left flex-1">
                                    <i className="pi pi-search" />
                                    <InputText
                                        value={vendorBillPaymentSearch}
                                        onChange={(event) => setVendorBillPaymentSearch(event.target.value)}
                                        placeholder={t('Buscar proveedor, número o cuenta')}
                                        className="w-full"
                                    />
                                </span>
                                <Dropdown
                                    value={vendorBillPaymentAccountFilter}
                                    options={vendorBillPaymentAccountOptions}
                                    onChange={(event) => setVendorBillPaymentAccountFilter(event.value)}
                                    placeholder={t('Cuenta de pago')}
                                    className="w-full md:w-20rem"
                                />
                            </div>
                            <Button
                                label={t('Exportar CSV')}
                                icon="pi pi-download"
                                className="p-button-outlined"
                                disabled={!filteredVendorBillPaymentRows.length}
                                onClick={() => vendorBillPaymentsTableRef.current?.exportCSV()}
                            />
                        </div>
                        <DataTable
                            ref={vendorBillPaymentsTableRef}
                            value={filteredVendorBillPaymentRows}
                            responsiveLayout="scroll"
                            loading={vendorBillPaymentsLoading}
                            emptyMessage={t('No hay pagos de proveedores para el período seleccionado')}
                            paginator={filteredVendorBillPaymentRows.length > 15}
                            rows={15}
                            dataKey="quickBooksId"
                            exportFilename={`pagos-proveedores-${selectedBusinessUnit}`}
                        >
                            <Column field="date" header={t('Fecha')} body={(rowData) => formatDateOnlyValue(rowData?.date)} sortable />
                            <Column field="number" header={t('Número')} body={(rowData) => rowData?.number || '-'} sortable />
                            <Column field="vendor" header={t('Proveedor')} sortable />
                            <Column field="account" header={t('Cuenta de pago')} sortable />
                            <Column field="paymentMethod" header={t('Método')} body={(rowData) => t(rowData?.paymentMethod || 'Sin especificar')} sortable />
                            <Column field="amount" header={t('Importe pagado')} body={(rowData) => formatMoney(rowData?.amount)} sortable />
                        </DataTable>
                        {Number(vendorBillPaymentTotals?.excludedZeroOrVoidedCount || 0) > 0 ? (
                            <div className="text-xs text-500 mt-2">
                                {formatCount(vendorBillPaymentTotals.excludedZeroOrVoidedCount)} {t('registros anulados o de importe cero fueron excluidos.')}
                            </div>
                        ) : null}
                    </Card>
                    <ReceivablesAgingCard
                        t={t}
                        title={t('Accounts Receivable Aging')}
                        collapsed={receivablesAgingCollapsed}
                        onToggle={() => setReceivablesAgingCollapsed((current) => !current)}
                        total={receivablesAgingTotal}
                        rows={receivablesAgingRows}
                        filteredRows={filteredReceivablesAgingRows}
                        filteredTotal={filteredReceivablesAgingTotal}
                        summary={receivablesAgingSummary}
                        bucketFilter={receivablesAgingBucketFilter}
                        bucketOptions={receivablesAgingBucketOptions}
                        onBucketChange={setReceivablesAgingBucketFilter}
                        onReviewEdit={isReadOnlyQuickBooksUser ? null : handleOpenReceivableReviewDialog}
                        savingReviewKey={savingReceivableReviewKey}
                        loading={commercialLoading}
                    />
                    <PayablesAgingCard
                        t={t}
                        title={t('Accounts Payable Aging')}
                        collapsed={payablesAgingCollapsed}
                        onToggle={() => setPayablesAgingCollapsed((current) => !current)}
                        total={payablesAgingTotal}
                        rows={payablesAgingRows}
                        filteredRows={filteredPayablesAgingRows}
                        filteredTotal={filteredPayablesAgingTotal}
                        summary={payablesAgingSummary}
                        bucketFilter={payablesAgingBucketFilter}
                        bucketOptions={payablesAgingBucketOptions}
                        onBucketChange={setPayablesAgingBucketFilter}
                        onAnnotationEdit={isReadOnlyQuickBooksUser ? null : handleOpenPayableAnnotationDialog}
                        savingAnnotationKey={savingPayableAnnotationKey}
                        loading={commercialLoading}
                    />
                    {selectedBusinessUnit === 'coffee_vending' ? (
                        <Card title={t('Conciliación Coffee & Vending')} className="mt-3">
                            <div className="grid mb-3">
                                <KpiCard
                                    title={t('Parlevel cobrado')}
                                    value={formatMoney(vendingReconciliationTotals.parlevelCollected)}
                                    hint={vendingReconciliation?.period?.label || financePeriodLabel}
                                    className="col-12 md:col-4"
                                />
                                <KpiCard
                                    title={t('QuickBooks cobrado')}
                                    value={formatMoney(vendingReconciliationTotals.quickBooksCollected)}
                                    hint={t('CustomerSalesDetail cash basis')}
                                    className="col-12 md:col-4"
                                />
                                <KpiCard
                                    title={t('Diferencia')}
                                    value={formatMoney(vendingReconciliationTotals.difference)}
                                    hint={`${t('Exactitud')}: ${formatPercent(vendingReconciliationTotals.accuracy)}`}
                                    className="col-12 md:col-4"
                                />
                            </div>
                            <DataTable
                                value={vendingReconciliationRows}
                                responsiveLayout="scroll"
                                loading={vendingReconciliationLoading}
                                emptyMessage={t('No reconciliation data available')}
                                paginator={vendingReconciliationRows.length > 10}
                                rows={10}
                            >
                                <Column field="period" header={t('Semana')} />
                                <Column field="parlevelCollected" header={t('Parlevel cobrado')} body={(rowData) => formatMoney(rowData?.parlevelCollected)} />
                                <Column field="quickBooksCollected" header={t('QuickBooks cobrado')} body={(rowData) => formatMoney(rowData?.quickBooksCollected)} />
                                <Column field="difference" header={t('Diferencia')} body={(rowData) => formatMoney(rowData?.difference)} />
                                <Column field="accuracy" header={t('Exactitud')} body={(rowData) => formatPercent(rowData?.accuracy)} />
                                <Column field="transactionCount" header={t('Transacciones')} body={(rowData) => formatCount(rowData?.transactionCount)} />
                                <Column
                                    field="status"
                                    header={t('Status')}
                                    body={(rowData) => <Tag value={t(rowData?.status || '-')} severity={getVendingReconciliationSeverity(rowData?.status)} />}
                                />
                            </DataTable>
                        </Card>
                    ) : null}
                </div>
            ) : null}

            {showDataPanel ? (
                <>
                    <div className="col-12">
                        <Card title={t('Connection Status')}>
                            {duplicateRealmWarnings.length > 0 ? (
                                <div className="mb-3">
                                    <Message
                                        severity="warn"
                                        text={t('Duplicate active QuickBooks realm assignments were detected across business units. Review the warnings column before reconnecting anything.')}
                                    />
                                </div>
                            ) : null}
                            <DataTable value={statusRows} responsiveLayout="scroll" emptyMessage={t('No business units available')}>
                                <Column field="label" header={t('Business unit')} />
                                <Column field="connection.realmId" header="Realm ID" body={(rowData) => rowData?.connection?.realmId || '-'} />
                                <Column
                                    header={t('Status')}
                                    body={(rowData) => (
                                        <Tag
                                            value={rowData?.connection?.health === 'warning' ? t('Warning') : t(getConnectionLabel(rowData?.connection))}
                                            severity={rowData?.connection?.health === 'warning' ? 'warning' : getConnectionSeverity(rowData?.connection)}
                                        />
                                    )}
                                />
                                <Column field="connection.connectedAt" header={t('Connected at')} body={(rowData) => formatDateTime(rowData?.connection?.connectedAt)} />
                                <Column field="connection.lastRefreshAt" header={t('Last refresh')} body={(rowData) => formatDateTime(rowData?.connection?.lastRefreshAt)} />
                                <Column
                                    header={t('Warnings')}
                                    body={(rowData) => {
                                        const warnings = Array.isArray(rowData?.connection?.warnings) ? rowData.connection.warnings : [];
                                        if (!warnings.length) return '-';
                                        return warnings.join(' | ');
                                    }}
                                />
                            </DataTable>
                        </Card>
                    </div>

                    {selectedBusinessUnit !== 'consolidated' ? (
                        <>
                            <div className="col-12 lg:col-4">
                                <Card title={t('Company')}>
                                    <div className="mb-3">
                                        <div className="text-sm text-600">{t('Company name')}</div>
                                        <div className="font-medium">{companyDetails?.CompanyName || '-'}</div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="text-sm text-600">{t('Legal name')}</div>
                                        <div className="font-medium">{companyDetails?.LegalName || '-'}</div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="text-sm text-600">{t('Country')}</div>
                                        <div className="font-medium">{companyDetails?.Country || '-'}</div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="text-sm text-600">{t('Email')}</div>
                                        <div className="font-medium">{companyDetails?.Email?.Address || '-'}</div>
                                    </div>
                                    <div className="flex gap-2 mt-4">
                                        <Button label={t('Run Sync')} icon="pi pi-cloud-download" onClick={handleRunSync} loading={syncing} disabled={isReadOnlyQuickBooksUser || !selectedConnection?.connected} />
                                        <Button
                                            label={t('Disconnect')}
                                            icon="pi pi-times"
                                            className="p-button-outlined p-button-danger"
                                            onClick={handleDisconnect}
                                            loading={disconnecting}
                                            disabled={isReadOnlyQuickBooksUser || !selectedConnection?.connected}
                                        />
                                    </div>
                                </Card>
                            </div>

                            <div className="col-12 lg:col-8">
                                <Card title={t('Sync Summary')}>
                                    <DataTable value={summaryCounts} responsiveLayout="scroll" emptyMessage={t('No sync summary available')}>
                                        <Column field="_id.entityType" header={t('Entity')} />
                                        <Column field="count" header={t('Records')} body={(rowData) => formatCount(rowData?.count)} />
                                        <Column field="lastQuickBooksUpdatedAt" header={t('Last QuickBooks update')} body={(rowData) => formatDateTime(rowData?.lastQuickBooksUpdatedAt)} />
                                        <Column field="lastSyncedAt" header={t('Last synced')} body={(rowData) => formatDateTime(rowData?.lastSyncedAt)} />
                                    </DataTable>
                                </Card>
                            </div>

                            <div className="col-12 lg:col-6">
                                <Card title={t('Recent Sync Logs')}>
                                    <DataTable value={syncLogs} responsiveLayout="scroll" emptyMessage={t('No sync logs available')}>
                                        <Column field="entityType" header={t('Entity')} />
                                        <Column
                                            field="status"
                                            header={t('Status')}
                                            body={(rowData) => (
                                                <Tag
                                                    value={tokenT(rowData?.status || '-')}
                                                    severity={rowData?.status === 'success' ? 'success' : rowData?.status === 'failed' ? 'danger' : 'info'}
                                                />
                                            )}
                                        />
                                        <Column field="mode" header={t('Mode')} />
                                        <Column field="fetched" header={t('Fetched')} body={(rowData) => formatCount(rowData?.fetched)} />
                                        <Column field="upserted" header={t('Upserted')} body={(rowData) => formatCount(rowData?.upserted)} />
                                        <Column field="startedAt" header={t('Started')} body={(rowData) => formatDateTime(rowData?.startedAt)} />
                                        <Column field="error" header={t('Error')} body={(rowData) => rowData?.error || '-'} />
                                    </DataTable>
                                </Card>
                            </div>

                            <div className="col-12 lg:col-6">
                                <Card title={t('Entities')}>
                                    <DataTable value={entities} responsiveLayout="scroll" emptyMessage={t('No entities available')}>
                                        <Column field="entityType" header={t('Entity')} />
                                        <Column field="displayName" header={t('Name')} />
                                        <Column field="quickBooksId" header="QuickBooks ID" />
                                        <Column field="balance" header={t('Balance')} body={(rowData) => (rowData?.balance == null ? '-' : formatCount(rowData.balance))} />
                                        <Column field="quickBooksUpdatedAt" header={t('Updated')} body={(rowData) => formatDateTime(rowData?.quickBooksUpdatedAt)} />
                                    </DataTable>
                                </Card>
                            </div>
                        </>
                    ) : null}
                </>
            ) : null}

            <Dialog
                header={t('Receivable review')}
                visible={!!receivableReviewDialogRow}
                style={{ width: '34rem', maxWidth: '96vw' }}
                modal
                onHide={handleCloseReceivableReviewDialog}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button type="button" label={t('Cancel')} icon="pi pi-times" className="p-button-text" onClick={handleCloseReceivableReviewDialog} />
                        <Button
                            type="button"
                            label={t('Save')}
                            icon="pi pi-save"
                            onClick={handleSaveReceivableReviewDialog}
                            loading={savingReceivableReviewKey === receivableReviewDialogRow?.quickBooksInvoiceId}
                        />
                    </div>
                }
            >
                <div className="mb-3">
                    <div className="text-sm text-600">{receivableReviewDialogRow?.customer || '-'}</div>
                    <div className="text-xl font-semibold text-900">{receivableReviewDialogRow?.invoice || '-'}</div>
                    <div className="text-sm text-600 mt-2">
                        {t('Open amount')}: {formatMoney(receivableReviewDialogRow?.amount)}
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12">
                        <label className="block text-900 font-medium mb-2">{t('Reviewed amount')}</label>
                        <InputNumber
                            value={Number(receivableReviewDraft.reviewedAmount || 0)}
                            onValueChange={(event) =>
                                setReceivableReviewDraft((current) => ({
                                    ...current,
                                    reviewedAmount: Math.min(Number(event.value || 0), Number(receivableReviewDialogRow?.amount || 0))
                                }))
                            }
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            min={0}
                            max={Number(receivableReviewDialogRow?.amount || 0)}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12">
                        <label className="block text-900 font-medium mb-2">{t('Payment probability')}</label>
                        <InputNumber
                            value={Number(receivableReviewDraft.paymentProbability || 0)}
                            onValueChange={(event) =>
                                setReceivableReviewDraft((current) => ({
                                    ...current,
                                    paymentProbability: Math.min(Math.max(Number(event.value || 0), 0), 100)
                                }))
                            }
                            suffix="%"
                            min={0}
                            max={100}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12">
                        <label className="block text-900 font-medium mb-2">{t('Expected collection date')}</label>
                        <Calendar
                            value={receivableReviewDraft.projectedCollectionDate}
                            onChange={(event) =>
                                setReceivableReviewDraft((current) => ({
                                    ...current,
                                    projectedCollectionDate: event.value || null
                                }))
                            }
                            dateFormat="yy-mm-dd"
                            showIcon
                            disabledDays={[0]}
                            minDate={parseDateOnly(cashFlowProjection?.forecastStartDate) || undefined}
                            maxDate={parseDateOnly(cashFlowProjection?.forecastEndDate) || undefined}
                            className="w-full"
                        />
                        <div className="text-xs text-500 mt-2">{t('Finance weeks run from Monday through Saturday.')}</div>
                    </div>
                    <div className="col-12">
                        <div className="p-3 border-1 border-200 border-round">
                            <div className="text-sm text-600 mb-1">{t('Expected collection')}</div>
                            <div className="text-2xl font-semibold text-900">
                                {formatMoney(Number(receivableReviewDraft.reviewedAmount || 0) * (Number(receivableReviewDraft.paymentProbability || 0) / 100))}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                header={t('Payable follow-up')}
                visible={!!payableAnnotationDialogRow}
                style={{ width: '32rem', maxWidth: '96vw' }}
                modal
                onHide={handleClosePayableAnnotationDialog}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button type="button" label={t('Cancel')} icon="pi pi-times" className="p-button-text" onClick={handleClosePayableAnnotationDialog} />
                        <Button
                            type="button"
                            label={t('Save')}
                            icon="pi pi-save"
                            onClick={handleSavePayableAnnotationDialog}
                            loading={savingPayableAnnotationKey === payableAnnotationDialogRow?.quickBooksBillId}
                        />
                    </div>
                }
            >
                <div className="mb-3">
                    <div className="text-sm text-600">{payableAnnotationDialogRow?.vendor || '-'}</div>
                    <div className="text-xl font-semibold text-900">{payableAnnotationDialogRow?.bill || '-'}</div>
                    <div className="text-sm text-600 mt-2">
                        {t('Open amount')}: {formatMoney(payableAnnotationDialogRow?.amount)}
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12">
                        <label className="block text-900 font-medium mb-2">{t('Payment status')}</label>
                        <Dropdown
                            value={payableAnnotationDraft.status}
                            options={PAYABLE_PAYMENT_STATUS_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
                            onChange={(event) => handlePayableAnnotationDraftStatusChange(event.value)}
                            className="w-full"
                        />
                    </div>
                    {payableAnnotationDraft.status === 'partial_paid' ? (
                        <div className="col-12">
                            <label className="block text-900 font-medium mb-2">{t('Partial payment')}</label>
                            <InputNumber
                                value={Number(payableAnnotationDraft.partialPaidAmount || 0)}
                                onValueChange={(event) =>
                                    setPayableAnnotationDraft((current) => ({
                                        ...current,
                                        partialPaidAmount: Math.min(Number(event.value || 0), Number(payableAnnotationDialogRow?.amount || 0))
                                    }))
                                }
                                mode="currency"
                                currency="USD"
                                locale="en-US"
                                min={0}
                                max={Number(payableAnnotationDialogRow?.amount || 0)}
                                className="w-full"
                            />
                            <div className="text-sm text-600 mt-2">
                                {t('Estimated remaining')}: {formatMoney(Math.max(Number(payableAnnotationDialogRow?.amount || 0) - Number(payableAnnotationDraft.partialPaidAmount || 0), 0))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </Dialog>

            <Dialog
                header={t('Editable assumptions')}
                visible={cashFlowSettingsVisible}
                style={{ width: '64rem', maxWidth: '96vw' }}
                modal
                onHide={() => setCashFlowSettingsVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            type="button"
                            icon="pi pi-undo"
                            label={t('Reset')}
                            onClick={handleResetCashFlowAssumptions}
                            className="p-button-outlined"
                        />
                        <Button
                            type="button"
                            icon="pi pi-refresh"
                            label={t('Apply')}
                            onClick={handleApplyCashFlowAssumptions}
                            loading={commercialLoading}
                        />
                    </div>
                }
            >
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <label className="block text-900 font-medium mb-2">{t('Minimum liquidity')}</label>
                        <InputNumber
                            value={cashFlowMinimumLiquidity}
                            onValueChange={(event) => setCashFlowMinimumLiquidity(Number(event.value || 0))}
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block text-900 font-medium mb-2">{t('Reserva autos')}</label>
                        <InputNumber
                            value={cashFlowVehicleMaintenanceReserve}
                            onValueChange={(event) => setCashFlowVehicleMaintenanceReserve(Number(event.value || 0))}
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <label className="block text-900 font-medium mb-2">{t('Vendor payment day 1')}</label>
                        <InputNumber
                            value={cashFlowVendorPaymentDays[0]}
                            onValueChange={(event) => handleCashFlowVendorPaymentDayChange(0, event.value)}
                            min={1}
                            max={31}
                            useGrouping={false}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <label className="block text-900 font-medium mb-2">{t('Vendor payment day 2')}</label>
                        <InputNumber
                            value={cashFlowVendorPaymentDays[1]}
                            onValueChange={(event) => handleCashFlowVendorPaymentDayChange(1, event.value)}
                            min={1}
                            max={31}
                            useGrouping={false}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <label className="block text-900 font-medium mb-2">{t('Fixed expense day')}</label>
                        <InputNumber
                            value={cashFlowFixedExpenseDay}
                            onValueChange={(event) => setCashFlowFixedExpenseDay(Number(event.value || 1))}
                            min={1}
                            max={31}
                            useGrouping={false}
                            className="w-full"
                        />
                    </div>
                    {['0-30', '31-60', '61-90', '90+'].map((bucket) => (
                        <div className="col-6 md:col-3" key={bucket}>
                            <label className="block text-900 font-medium mb-2">{`${t('Probabilidad')} ${bucket}`}</label>
                            <InputNumber
                                value={cashFlowCollectionProbabilities[bucket]}
                                onValueChange={(event) => handleCashFlowProbabilityChange(bucket, event.value)}
                                suffix="%"
                                min={0}
                                max={100}
                                className="w-full"
                            />
                        </div>
                    ))}
                </div>
                <div className="grid mt-2">
                    {[0, 1, 2, 3].map((weekIndex) => (
                        <div className="col-12 md:col-6 xl:col-3" key={`manual-week-${weekIndex}`}>
                            <div className="text-sm font-semibold text-700 mb-2">{`${t('Week')} ${weekIndex + 1}`}</div>
                            <label className="block text-600 mb-2">{t('Manual collections')}</label>
                            <InputNumber
                                value={cashFlowManualCollections[weekIndex]}
                                onValueChange={(event) => handleCashFlowWeeklyValueChange(setCashFlowManualCollections, weekIndex, event.value)}
                                mode="currency"
                                currency="USD"
                                locale="en-US"
                                className="w-full mb-2"
                            />
                            <label className="block text-600 mb-2">{t('Manual payments')}</label>
                            <InputNumber
                                value={cashFlowManualPayments[weekIndex]}
                                onValueChange={(event) => handleCashFlowWeeklyValueChange(setCashFlowManualPayments, weekIndex, event.value)}
                                mode="currency"
                                currency="USD"
                                locale="en-US"
                                className="w-full"
                            />
                        </div>
                    ))}
                </div>
            </Dialog>

            <Dialog
                header={t('Customer Quarterly Quotas')}
                visible={customerQuotaDialogVisible}
                style={{ width: '72rem', maxWidth: '96vw' }}
                modal
                onHide={() => setCustomerQuotaDialogVisible(false)}
            >
                <div className="grid align-items-end mb-3">
                    <div className="col-12 md:col-3">
                        <label className="block text-900 font-medium mb-2">{t('Business unit')}</label>
                        <div className="p-inputtext p-component w-full">{businessUnitOptions.find((option) => option.value === selectedBusinessUnit)?.label || '-'}</div>
                    </div>
                    <div className="col-12 md:col-2">
                        <label className="block text-900 font-medium mb-2">{t('Year')}</label>
                        <InputNumber
                            value={customerQuotaYear}
                            onValueChange={(e) => setCustomerQuotaYear(e.value || new Date().getFullYear())}
                            useGrouping={false}
                            min={2024}
                            max={2100}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <label className="block text-900 font-medium mb-2">{t('Quarter')}</label>
                        <Dropdown value={customerQuotaQuarter} options={QUARTER_OPTIONS} onChange={(e) => setCustomerQuotaQuarter(e.value)} className="w-full" />
                    </div>
                    <div className="col-12 md:col-5 flex justify-content-end gap-2">
                        <Button
                            label={t('Reload')}
                            icon="pi pi-refresh"
                            className="p-button-outlined"
                            onClick={fetchCustomerQuarterQuotaEditor}
                            loading={customerQuotaLoading}
                        />
                        <Button
                            label={t('Even split')}
                            icon="pi pi-percentage"
                            className="p-button-outlined"
                            onClick={handleApplyCustomerEvenSplit}
                            disabled={isReadOnlyQuickBooksUser || !customerQuotaRows.length}
                        />
                        <Button label={t('Save')} icon="pi pi-save" onClick={handleSaveCustomerQuotas} loading={customerQuotaSaving} disabled={isReadOnlyQuickBooksUser || customerQuotaLoading} />
                    </div>
                </div>
                <div className="mb-3">
                    <Message severity="info" text={t('Enter the quarterly quota per customer. Monthly allocations can be split evenly or adjusted manually.')} />
                </div>
                <DataTable
                    value={customerQuotaRows}
                    responsiveLayout="scroll"
                    loading={customerQuotaLoading}
                    paginator
                    rows={10}
                    emptyMessage={t('No customer data available')}
                >
                    <Column field="customer" header={t('Customer')} />
                    <Column field="netSales" header={t('Net sales')} body={(rowData) => formatMoney(rowData?.netSales)} />
                    <Column
                        header={t('Quarter total')}
                        body={(rowData) => (
                            <InputNumber
                                value={rowData?.quarterlyQuota || 0}
                                disabled={isReadOnlyQuickBooksUser}
                                onValueChange={(e) => handleCustomerQuotaChange(rowData.customer, 'quarterlyQuota', e.value)}
                                mode="currency"
                                currency="USD"
                                locale="en-US"
                                min={0}
                                className="w-full"
                            />
                        )}
                    />
                    {customerQuotaMonths.map((periodMonth, index) => (
                        <Column
                            key={periodMonth}
                            header={periodMonth}
                            body={(rowData) => (
                                <InputNumber
                                    value={rowData?.monthlyAllocations?.[index] || 0}
                                    disabled={isReadOnlyQuickBooksUser}
                                onValueChange={(e) => handleCustomerQuotaChange(rowData.customer, 'monthlyAllocation', e.value, index)}
                                    mode="currency"
                                    currency="USD"
                                    locale="en-US"
                                    min={0}
                                    className="w-full"
                                />
                            )}
                        />
                    ))}
                    <Column field="attainment" header={t('Attainment')} body={(rowData) => formatPercent(rowData?.attainment)} />
                </DataTable>
            </Dialog>

            <Dialog
                header={t('Quarter Quotas')}
                visible={quotaDialogVisible}
                style={{ width: '72rem', maxWidth: '96vw' }}
                modal
                onHide={() => setQuotaDialogVisible(false)}
            >
                <div className="grid align-items-end mb-3">
                    <div className="col-12 md:col-3">
                        <label className="block text-900 font-medium mb-2">{t('Business unit')}</label>
                        <div className="p-inputtext p-component w-full">{businessUnitOptions.find((option) => option.value === selectedBusinessUnit)?.label || '-'}</div>
                    </div>
                    <div className="col-12 md:col-2">
                        <label className="block text-900 font-medium mb-2">{t('Year')}</label>
                        <InputNumber value={quotaYear} onValueChange={(e) => setQuotaYear(e.value || new Date().getFullYear())} useGrouping={false} min={2024} max={2100} className="w-full" />
                    </div>
                    <div className="col-12 md:col-2">
                        <label className="block text-900 font-medium mb-2">{t('Quarter')}</label>
                        <Dropdown value={quotaQuarter} options={QUARTER_OPTIONS} onChange={(e) => setQuotaQuarter(e.value)} className="w-full" />
                    </div>
                    <div className="col-12 md:col-5 flex justify-content-end gap-2">
                        <Button label={t('Reload')} icon="pi pi-refresh" className="p-button-outlined" onClick={fetchQuarterQuotaEditor} loading={quarterQuotaLoading} />
                        <Button label={t('Even split')} icon="pi pi-percentage" className="p-button-outlined" onClick={handleApplyEvenSplit} disabled={isReadOnlyQuickBooksUser || !quarterQuotaRows.length} />
                        <Button label={t('Save')} icon="pi pi-save" onClick={handleSaveQuarterQuotas} loading={quarterQuotaSaving} disabled={isReadOnlyQuickBooksUser || quarterQuotaLoading} />
                    </div>
                </div>

                <div className="mb-3">
                    <Message
                        severity="info"
                        text={t('Enter the quarterly quota per seller. Monthly allocations can stay evenly split or be adjusted manually.')}
                    />
                </div>

                <DataTable value={quarterQuotaRows} responsiveLayout="scroll" loading={quarterQuotaLoading} emptyMessage={t('No sellers available for this business unit')}>
                    <Column field="seller" header={t('Seller')} />
                    <Column
                        header={t('Quarter total')}
                        body={(rowData) => (
                            <InputNumber
                                value={rowData?.quarterlyQuota || 0}
                                disabled={isReadOnlyQuickBooksUser}
                                onValueChange={(e) => handleQuarterQuotaChange(rowData.seller, 'quarterlyQuota', e.value)}
                                mode="currency"
                                currency="USD"
                                locale="en-US"
                                min={0}
                                className="w-full"
                            />
                        )}
                    />
                    {quarterQuotaMonths.map((periodMonth, index) => (
                        <Column
                            key={periodMonth}
                            header={formatPeriodMonthLabel(periodMonth)}
                            body={(rowData) => (
                                <InputNumber
                                    value={rowData?.monthlyAllocations?.[index] || 0}
                                    disabled={isReadOnlyQuickBooksUser}
                                onValueChange={(e) => handleQuarterQuotaChange(rowData.seller, 'monthlyAllocation', e.value, index)}
                                    mode="currency"
                                    currency="USD"
                                    locale="en-US"
                                    min={0}
                                    className="w-full"
                                />
                            )}
                        />
                    ))}
                </DataTable>
            </Dialog>
        </div>
    );
};

export default QuickBooksModule;
